import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { MapContainer, Marker } from 'react-leaflet';
import {
  FiArrowLeft, FiEdit, FiEdit2, FiEdit3, FiExternalLink, FiMapPin, FiPhone, FiMail,
  FiPlus, FiTrash2, FiUpload, FiCamera, FiShare2, FiPaperclip, FiX, FiDownload,
  FiMessageSquare, FiSend, FiCornerUpLeft, FiRefreshCw, FiChevronDown, FiChevronLeft, FiChevronRight, FiLink, FiPrinter, FiFolder,
  FiUsers, FiFileText, FiSearch, FiSlash
} from 'react-icons/fi';
import {
  getJob, updateJob, getChangeOrders, createChangeOrder, updateChangeOrder, deleteChangeOrder, getAuthToken,
  getJobParcels, deleteJobParcel, clearJobParcels, uploadParcelsJson
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { BasemapLayers, BasemapToggle } from '../components/MapBasemaps';
import JobFieldEditModal from '../components/JobFieldEditModal';
import ParentJobModal from '../components/ParentJobModal';
import PlacePhotosModal from '../components/PlacePhotosModal';
import WorkOrderAttachmentsModal from '../components/WorkOrderAttachmentsModal';
import FormModal from '../components/FormModal';
import JobAttachments from '../components/JobAttachments';
import EmailToClientModal from '../components/EmailToClientModal';
import JobLocationPickerModal from '../components/JobLocationPickerModal';
import JobComments from '../components/JobComments';
import RelatedLetters from '../components/RelatedLetters';
import FieldChecklistModal from '../components/FieldChecklistModal';
import ParcelImportModal from '../components/ParcelImportModal';
import JobTimer from '../components/JobTimer';
import WorkOrderPrint from '../components/WorkOrderPrint';
import SelfAssignButton from '../components/SelfAssignButton';
import EmailEditor from '../components/EmailEditor';
import { API_URL, API_BASE_URL } from '../config';
import PhotoAnnotator from '../components/PhotoAnnotator';
import EmailBody from '../components/EmailBody';
import { appendDeviceLocation } from '../utils/geotag';
import { useQueuedPhotos } from '../hooks/useQueuedPhotos';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

const STATUSES = [
  'Pending', 'Scheduled', 'In Progress - Office Work', 'In Progress - Initial Field Work',
  'In Progress - Topo', 'In Progress - Additional Field Work Required', 'In Progress - Drafting',
  'On Hold', 'Survey Complete', 'Needs Review', 'Sent for Review',
  'Approved - Internal Review', 'Approved - Planning & Zoning',
  'Submitted for Recording', 'Recorded',
  'Final Mark Up / Stake Lines', 'Needs Invoice', 'Invoiced', 'Paid'
];

// A job saved under a retired status (the old bare 'Approved', 'Imported', legacy
// 'In Progress - Field Work') must still show its own value, or the select renders
// blank and the next save silently rewrites the status to whatever sits at the top.
const withCurrent = (list, current) => (current && !list.includes(current) ? [...list, current] : list);

const WORK_TYPE_OPTIONS = [
  '', 'Field Work', 'Drafting', 'Final Mark Up / Stake Lines', 'Office Work', 'Research', 'Travel', 'Review'
];

const SERVICE_TYPE_OPTIONS = [
  '', 'Boundary', 'Topographic', 'ALTA', 'Construction Stakeout', 'As-Built',
  'Foundation', 'Subdivision/Plat', 'Elevation Certificate', 'Mortgage Inspection', 'Other'
];

const SUBMITTAL_OPTIONS = [
  '', 'Not Submitted', 'Submitted to Client', 'Submitted to County/City',
  'In Review', 'Revisions Requested', 'Approved', 'Recorded'
];
const STATUS_COLORS = {
  'Pending': '#fbbf24', 'Scheduled': '#60a5fa', 'In Progress - Office Work': '#818cf8',
  'In Progress - Initial Field Work': '#a78bfa', 'In Progress - Topo': '#a78bfa',
  'In Progress - Additional Field Work Required': '#7c3aed', 'In Progress - Drafting': '#8b5cf6',
  'Survey Complete': '#34d399', 'Needs Review': '#f97316', 'Sent for Review': '#fb923c',
  'Approved': '#22c55e', 'Approved - Internal Review': '#4ade80',
  'Approved - Planning & Zoning': '#22c55e', 'Submitted for Recording': '#14b8a6',
  'Recorded': '#0d9488',
  'Final Mark Up / Stake Lines': '#ec4899', 'Needs Invoice': '#eab308',
  'Invoiced': '#fb923c', 'Paid': '#10b981', 'Cancelled': '#9ca3af', 'On Hold': '#64748b',
};
const statusColor = (s) => STATUS_COLORS[s] || '#6b7280';

const fmtCents = (n) => '$' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMoney = (n) => '$' + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;
const fmtTime = (t) => {
  if (!t) return null;
  const [h, m] = t.split(':');
  const hr = (parseInt(h, 10) % 12) || 12;
  return `${hr}:${m} ${parseInt(h, 10) < 12 ? 'AM' : 'PM'}`;
};

const getAuthConfig = async () => {
  const token = await getAuthToken();
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

function InfoGroup({ title, rows, onEdit }) {
  const visible = rows.filter(r => r && r.value != null && r.value !== '');
  if (visible.length === 0 && !onEdit) return null;
  return (
    <div className="qv-group">
      <div className="qv-group-title">
        <span>{title}</span>
        {onEdit && (
          <button className="qv-edit-btn" onClick={onEdit} title={`Edit ${title}`}>
            <FiEdit2 size={13} />
          </button>
        )}
      </div>
      {visible.length > 0
        ? visible.map((r, i) => (
            <div className="qv-row" key={i}>
              <span className="qv-label">{r.label}</span>
              <span className="qv-value">{r.value}</span>
            </div>
          ))
        : <div className="qv-empty-hint">No details yet — click ✎ to add</div>}
    </div>
  );
}

const PRIORITY_OPTIONS = ['Low', 'Normal', 'High', 'Urgent'];
const escapeHtml = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const plainToHtml = (s) => escapeHtml(s).replace(/\n/g, '<br>');

// Crew close-out answers are stored as short codes; these are the words. Must stay in
// step with MONUMENT_RECOVERY_VALUES / SITE_CONDITION_VALUES in app.py and with the
// options in RequiredCommentModal.
const MONUMENT_OPTIONS = [
  { value: '', label: '' },
  { value: 'most', label: 'Found most' },
  { value: 'some', label: 'Found some' },
  { value: 'none', label: 'Almost none — rebuilt from deeds' },
];
const SITE_OPTIONS = [
  { value: '', label: '' },
  { value: 'open', label: 'Open — clear shots' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'heavy', label: 'Heavy — cutting line / no GPS' },
];
const optLabel = (opts, v) => (opts.find((o) => o.value === v) || {}).label || null;

const SECTION_FIELDS = {
  location: { title: 'Map Location', fields: [
    { key: 'latitude', label: 'Latitude (decimal degrees)', type: 'text', placeholder: 'e.g. 34.812345' },
    { key: 'longitude', label: 'Longitude (decimal degrees)', type: 'text', placeholder: 'e.g. -84.971234' },
  ] },
  serverFolders: { title: 'Server Folders (office only)', fields: [
    { key: 'job_folder_path', label: 'Job Folder path', type: 'text', placeholder: '\\\\NGS-SERVER\\Jobs\\J4692 …  (or Z:\\Jobs\\J4692)' },
    { key: 'property_records_path', label: 'Property Records path', type: 'text', placeholder: '\\\\NGS-SERVER\\PropertyRecords\\Pickens\\…' },
  ] },
  siteAccess: { title: 'Site Access', fields: [
    { key: 'location_person', label: 'On-Site Contact', type: 'text' },
    { key: 'access_code', label: 'Access Code', type: 'text' },
    { key: 'flood_zone', label: 'Flood Zone', type: 'text' },
    { key: 'job_specific_notes', label: 'Access Notes', type: 'textarea' },
  ] },
  projectInfo: { title: 'Project Information', fields: [
    { key: 'service_type', label: 'Type', type: 'select', options: SERVICE_TYPE_OPTIONS },
    { key: 'description_of_work', label: 'Description', type: 'textarea' },
    { key: 'po_number', label: 'Purchase Order #', type: 'text' },
    { key: 'legal_description', label: 'Legal Description', type: 'textarea' },
    { key: 'notes', label: 'Notes', type: 'textarea' },
  ] },
  conditions: { title: 'Field Conditions', fields: [
    { key: 'corners_count', label: 'Corners Set / Recovered', type: 'number' },
    { key: 'monument_recovery', label: 'Existing Monuments', type: 'select', options: MONUMENT_OPTIONS },
    { key: 'site_conditions', label: 'Terrain & Vegetation', type: 'select', options: SITE_OPTIONS },
  ] },
  lotTract: { title: 'Lot(s) / Tract', fields: [
    { key: 'property_address', label: 'Street Address', type: 'text' },
    { key: 'location_city_state_zip', label: 'City/State/Zip', type: 'text' },
    { key: 'parcel', label: 'Parcel', type: 'text' },
    // Acreage actually surveyed. qPublic often reports the parent tract a survey is
    // cut out of, so this override exists to correct it. Blank falls back to the
    // linked quote, then parcel records.
    { key: 'acres', label: 'Acres Surveyed', type: 'number' },
    { key: 'subdivision', label: 'Subdivision', type: 'text' },
    { key: 'county', label: 'County', type: 'text' },
    { key: 'land_lot', label: 'Land Lot', type: 'text' },
    { key: 'district', label: 'District', type: 'text' },
    { key: 'section', label: 'Section', type: 'text' },
    { key: 'gmd', label: 'GMD', type: 'text' },
  ] },
  tracking: { title: 'Project Tracking', fields: [
    { key: 'status', label: 'Status', type: 'select', options: STATUSES },
    { key: 'priority', label: 'Priority', type: 'select', options: PRIORITY_OPTIONS },
    { key: 'submittal_status', label: 'Submittal Status', type: 'select', options: SUBMITTAL_OPTIONS },
  ] },
  dates: { title: 'Project Dates', fields: [
    { key: 'work_ordered_date', label: 'Work Ordered', type: 'date' },
    { key: 'scheduled_date', label: 'Scheduled', type: 'date' },
    { key: 'work_started_date', label: 'Started', type: 'date' },
    { key: 'work_finished_date', label: 'Finished', type: 'date' },
    { key: 'completion_date', label: 'Completed', type: 'date' },
  ] },
};

const TABS = ['Details', 'Phases', 'Proposals & Services', 'Contacts', 'Time Entries', 'Checklists', 'Parcels', 'Documents', 'Pictures', 'Letters', 'Comments', 'Portal', 'Profitability', 'Finances'];

export default function JobViewV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Where did the user come from? Set via Link state={{from, label}} on the calling page.
  // Falls back to sessionStorage so a page refresh keeps the correct back target.
  const stateFrom = location.state?.from;
  const stateLabel = location.state?.label;
  const _backStorageKey = `jobBack_${id}`;
  if (stateFrom && stateLabel) {
    try { sessionStorage.setItem(_backStorageKey, JSON.stringify({ from: stateFrom, label: stateLabel })); } catch (e) {}
  }
  let _savedBack = null;
  try {
    const raw = sessionStorage.getItem(_backStorageKey);
    if (raw) _savedBack = JSON.parse(raw);
  } catch (e) {}
  const backPath = stateFrom || _savedBack?.from || '/jobs';
  const backLabel = stateLabel || _savedBack?.label || 'Jobs';
  const { user } = useAuth();
  const canManagePayroll = user?.can_manage_payroll || false;
  // Quotes are a separate grant: a CAD tech can write proposals without seeing payroll or
  // contract figures. Gating the quote button on canManagePayroll would hide it from
  // exactly the people who write quotes.
  const canEditQuotes = user?.can_edit_quotes || canManagePayroll;
  // Office staff (not field crews) — server-folder links only work on the office network.
  const isOffice = canManagePayroll || /cad|office|admin|owner|manager|drafter/i.test(user?.role || '');
  // Open a local/network folder via the ngsfolder:// protocol handler (installed per office PC).
  const openServerFolder = (path) => { if (path) window.location.href = `ngsfolder:${encodeURIComponent(path)}`; };
  const copyServerPath = async (path) => {
    if (!path) return;
    try { await navigator.clipboard.writeText(path); alert('Path copied — paste into File Explorer.'); }
    catch (e) { window.prompt('Folder path:', path); }
  };
  const canViewRecordings = user?.can_view_call_recordings || false;

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Details');
  const [basemap, setBasemap] = useState('satellite');
  const [editSection, setEditSection] = useState(null); // { title, fields } for the section pencil
  const [editParent, setEditParent] = useState(false);  // parent-job link modal
  const [showPlacePhotos, setShowPlacePhotos] = useState(false); // place-photos-on-map modal
  const [woAttachments, setWoAttachments] = useState(null); // work order whose attachments modal is open

  // Lazy tab data
  const [contacts, setContacts] = useState(null);
  const [calls, setCalls] = useState(null);
  const [callingId, setCallingId] = useState(null);
  const [mediaToken, setMediaToken] = useState('');
  const [allClients, setAllClients] = useState([]);   // for linking a subcontractor to a client record
  // "Add from Company" picker — the client's saved roster (foreman, AP, PM)
  const [rosterOpen, setRosterOpen] = useState(false);
  const [roster, setRoster] = useState({ client_label: '', candidates: [] });
  const [rosterPicked, setRosterPicked] = useState([]);
  const [rosterBusy, setRosterBusy] = useState(false);
  const [messages, setMessages] = useState(null);
  const [smsTo, setSmsTo] = useState(null);   // contact we're texting
  const [smsBody, setSmsBody] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [emails, setEmails] = useState(null);
  const [emailTo, setEmailTo] = useState(null);       // contact we're emailing
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');        // plain-text fallback
  const [emailBodyHtml, setEmailBodyHtml] = useState(''); // rich-text HTML (what's sent)
  const [emailFiles, setEmailFiles] = useState([]);       // attachments
  const [sendingEmail, setSendingEmail] = useState(false);
  const [openEmailId, setOpenEmailId] = useState(null);
  const [emailReplyToId, setEmailReplyToId] = useState(null);  // threading
  const [moveEmailFor, setMoveEmailFor] = useState(null);      // email whose "move to another job" picker is open
  const [moveQuery, setMoveQuery] = useState('');
  const [moveHits, setMoveHits] = useState([]);
  const [showTexts, setShowTexts] = useState(true);            // collapse the text thread
  const [showEmailList, setShowEmailList] = useState(true);    // collapse the email list
  const [editingNumber, setEditingNumber] = useState(false);   // inline-edit the job number
  const [numberDraft, setNumberDraft] = useState('');
  const [savingNumber, setSavingNumber] = useState(false);
  const [timeEntries, setTimeEntries] = useState(null);
  const [showWorkOrder, setShowWorkOrder] = useState(false);
  // Ported-from-classic features
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [uploadingParcel, setUploadingParcel] = useState(false);
  const [expenses, setExpenses] = useState(null);
  const [savingExpenses, setSavingExpenses] = useState(false);
  const [enRouteBusy, setEnRouteBusy] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [notifySel, setNotifySel] = useState([]);
  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyMsg, setNotifyMsg] = useState('');
  const [notifySms, setNotifySms] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [photos, setPhotos] = useState(null);
  // Shots taken without signal, still waiting in the outbox. Shown newest-first
  // alongside synced photos so the crew can see the shot landed.
  const queuedPhotos = useQueuedPhotos(id);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [bulkDl, setBulkDl] = useState(null); // { done, total } while saving all photos
  const [workOrders, setWorkOrders] = useState(null);
  const [woFilter, setWoFilter] = useState('open');  // 'open' = everything except Paid
  const [changeOrders, setChangeOrders] = useState(null);
  const [profit, setProfit] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [jobInvoices, setJobInvoices] = useState(null);
  const [checklists, setChecklists] = useState(null);
  const [editingChecklist, setEditingChecklist] = useState(null); // { id }
  const [parcels, setParcels] = useState(null);
  const [portal, setPortal] = useState(null);
  const [portalNotifs, setPortalNotifs] = useState([]);
  const [uploadingParcels, setUploadingParcels] = useState(false);
  const [uploadingBoundary, setUploadingBoundary] = useState(false);
  const [parcelImportOpen, setParcelImportOpen] = useState(false);

  const loadJob = useCallback(async () => {
    try {
      const res = await getJob(id);
      setJob(res.data);
    } catch (e) {
      console.error('Error loading job:', e);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { loadJob(); }, [loadJob]);
  useEffect(() => { getAuthToken().then(t => setMediaToken(t || '')); }, []);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/clients`, await getAuthConfig());
        const list = Array.isArray(res.data) ? res.data : (res.data?.clients || []);
        setAllClients(list);
      } catch (e) { /* non-fatal — the link-to-client picker just stays empty */ }
    })();
  }, []);

  // Named loaders (callable after mutations to refresh a tab)
  const asArray = (d, key) => Array.isArray(d) ? d : (d && Array.isArray(d[key]) ? d[key] : []);
  const loadContacts = useCallback(async () => {
    const res = await axios.get(`${API_URL}/jobs/${id}/contacts`, await getAuthConfig());
    setContacts(asArray(res.data, 'contacts'));
  }, [id]);
  const loadCalls = useCallback(async () => {
    const res = await axios.get(`${API_URL}/jobs/${id}/calls`, await getAuthConfig());
    setCalls(asArray(res.data, 'calls'));
  }, [id]);
  const loadMessages = useCallback(async () => {
    const res = await axios.get(`${API_URL}/jobs/${id}/sms`, await getAuthConfig());
    setMessages(asArray(res.data, 'messages'));
  }, [id]);
  const loadEmails = useCallback(async () => {
    const res = await axios.get(`${API_URL}/jobs/${id}/emails`, await getAuthConfig());
    setEmails(asArray(res.data, 'emails'));
  }, [id]);
  const sendJobEmail = useCallback(async () => {
    if (!emailTo || !emailBody.trim()) return;
    setSendingEmail(true);
    try {
      const fd = new FormData();
      fd.append('to', emailTo.email);
      fd.append('subject', emailSubject.trim());
      fd.append('body', emailBody.trim());          // plain-text fallback
      fd.append('body_html', emailBodyHtml || '');  // rich text (what's sent)
      if (emailReplyToId) fd.append('in_reply_to_id', emailReplyToId);
      emailFiles.forEach(f => fd.append('files', f));
      await axios.post(`${API_URL}/jobs/${id}/emails`, fd, await getAuthConfig());
      setEmailBody(''); setEmailBodyHtml(''); setEmailFiles([]); setEmailSubject(''); setEmailTo(null); setEmailReplyToId(null);
      loadEmails();
    } catch (e) {
      window.alert(e.response?.data?.error || 'Could not send the email.');
    } finally {
      setSendingEmail(false);
    }
  }, [id, emailTo, emailSubject, emailBody, emailBodyHtml, emailFiles, emailReplyToId, loadEmails]);
  const sendSms = useCallback(async () => {
    if (!smsTo || !smsBody.trim()) return;
    setSendingSms(true);
    try {
      await axios.post(`${API_URL}/jobs/${id}/sms`, { phone: smsTo.phone, name: smsTo.name, body: smsBody.trim() }, await getAuthConfig());
      setSmsBody('');
      setSmsTo(null);
      loadMessages();
    } catch (e) {
      window.alert(e.response?.data?.error || 'Could not send the text.');
    } finally {
      setSendingSms(false);
    }
  }, [id, smsTo, smsBody, loadMessages]);
  const deleteJobEmail = useCallback(async (em) => {
    const touchesInbox = em.direction === 'in' && em.mailbox_owner_id === user?.id;
    const prompt = touchesInbox
      ? 'Delete this email?\n\nIt will also be moved to Trash in your inbox — recoverable there for ~30 days.'
      : 'Delete this email from the app?';
    if (!window.confirm(prompt)) return;
    try {
      await axios.delete(`${API_URL}/emails/${em.id}`, await getAuthConfig());
      loadEmails();
    } catch (e) {
      window.alert(e.response?.data?.error || 'Could not delete.');
    }
  }, [user, loadEmails]);
  // Filed to the wrong job — move it, or take it off this job entirely.
  const closeMovePicker = useCallback(() => {
    setMoveEmailFor(null); setMoveQuery(''); setMoveHits([]);
  }, []);
  const moveJobEmail = useCallback(async (em, link) => {
    const jobId = parseInt(String(link).split('/').pop(), 10);
    if (!jobId) return;
    try {
      await axios.post(`${API_URL}/emails/${em.id}/assign`, { job_id: jobId }, await getAuthConfig());
      closeMovePicker();
      setOpenEmailId(null);
      loadEmails();
    } catch (e) {
      window.alert(e.response?.data?.error || 'Could not move the email.');
    }
  }, [loadEmails, closeMovePicker]);
  const unfileJobEmail = useCallback(async (em) => {
    if (!window.confirm('Take this email off this job?\n\nThe email is kept — incoming mail goes back to "Needs filing" on the Email page.')) return;
    try {
      await axios.post(`${API_URL}/emails/${em.id}/unassign`, {}, await getAuthConfig());
      closeMovePicker();
      setOpenEmailId(null);
      loadEmails();
    } catch (e) {
      window.alert(e.response?.data?.error || 'Could not remove it from the job.');
    }
  }, [loadEmails, closeMovePicker]);
  // Job lookup for the move picker — same global search the rest of the app uses.
  useEffect(() => {
    if (moveQuery.trim().length < 2) { setMoveHits([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/search?q=${encodeURIComponent(moveQuery.trim())}`, await getAuthConfig());
        const jobs = (res.data.groups || []).find(g => g.type === 'jobs');
        setMoveHits(jobs ? jobs.items : []);
      } catch (e) { setMoveHits([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [moveQuery]);
  const placeCall = useCallback(async (c) => {
    if (!c.phone) return;
    setCallingId(c.id || 'primary');
    try {
      const res = await axios.post(`${API_URL}/jobs/${id}/call`, { phone: c.phone, name: c.name }, await getAuthConfig());
      window.alert(res.data?.message || `Calling your phone — answer to connect to ${c.name}.`);
      loadCalls();
    } catch (e) {
      window.alert(e.response?.data?.error || 'Could not place the call.');
    } finally {
      setCallingId(null);
    }
  }, [id, loadCalls]);
  // While viewing Contacts, poll the message thread + call log so client replies and
  // inbound calls appear live without a manual refresh. (Declared after the loaders it
  // uses so it doesn't reference them before initialization.)
  useEffect(() => {
    if (activeTab !== 'Contacts') return undefined;
    const iv = setInterval(() => { loadMessages(); loadCalls(); loadEmails(); }, 15000);
    return () => clearInterval(iv);
  }, [activeTab, loadMessages, loadCalls, loadEmails]);
  const loadTimeEntries = useCallback(async () => {
    const res = await axios.get(`${API_URL}/time-entries?type=job&job_id=${id}`, await getAuthConfig());
    setTimeEntries(asArray(res.data, 'time_entries'));
  }, [id]);
  const loadPhotos = useCallback(async () => {
    const res = await axios.get(`${API_URL}/jobs/${id}/photos`, await getAuthConfig());
    setPhotos(asArray(res.data, 'photos'));
  }, [id]);
  const allPhotos = useMemo(
    () => [...queuedPhotos, ...(photos || [])],
    [queuedPhotos, photos]
  );
  // Only synced photos open in the lightbox (queued ones are local blob URLs), so
  // prev/next steps through this list rather than allPhotos.
  const viewablePhotos = useMemo(() => allPhotos.filter(p => !p.__queued), [allPhotos]);
  const swipeRef = useRef(null); // touch start point for lightbox swipe nav
  // Arrow keys step through photos and Escape closes the lightbox on desktop.
  useEffect(() => {
    if (!lightboxPhoto) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { setLightboxPhoto(null); return; }
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (viewablePhotos.length < 2) return;
      const i = viewablePhotos.findIndex(p => p.id === lightboxPhoto.id);
      if (i === -1) return;
      e.preventDefault();
      const step = e.key === 'ArrowLeft' ? -1 : 1;
      setLightboxPhoto(viewablePhotos[(i + step + viewablePhotos.length) % viewablePhotos.length]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxPhoto, viewablePhotos]);

  const loadWorkOrders = useCallback(async () => {
    const res = await axios.get(`${API_URL}/jobs/${id}/work-orders`, await getAuthConfig());
    setWorkOrders(asArray(res.data, 'work_orders'));
  }, [id]);
  const loadChangeOrders = useCallback(async () => {
    const co = await getChangeOrders(id);
    setChangeOrders(asArray(co.data, 'change_orders'));
  }, [id]);
  const loadProfit = useCallback(async () => {
    const config = await getAuthConfig();
    // The estimate is a nice-to-have beside the real numbers; never let it block them.
    axios.get(`${API_URL}/jobs/${id}/estimate`, config).then(r => setEstimate(r.data)).catch(() => setEstimate(null));
    const res = await axios.get(`${API_URL}/jobs/${id}/profitability`, config);
    setProfit(res.data);
  }, [id]);
  const loadJobInvoices = useCallback(async () => {
    const res = await axios.get(`${API_URL}/invoices?job_id=${id}`, await getAuthConfig());
    setJobInvoices(res.data?.invoices || []);
  }, [id]);
  const loadChecklists = useCallback(async () => {
    const res = await axios.get(`${API_URL}/jobs/${id}/field-checklists`, await getAuthConfig());
    setChecklists(asArray(res.data, 'field_checklists') || asArray(res.data, 'checklists'));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadParcels = useCallback(async () => {
    const res = await getJobParcels(id);
    setParcels(asArray(res.data, 'parcels'));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadPortal = useCallback(async () => {
    const cfg = await getAuthConfig();
    const [p, n] = await Promise.all([
      axios.get(`${API_URL}/jobs/${id}/portal`, cfg),
      axios.get(`${API_URL}/jobs/${id}/portal/notifications`, cfg),
    ]);
    setPortal(p.data);
    setPortalNotifs(asArray(n.data, 'notifications'));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load per-tab data on demand (first visit to a tab)
  useEffect(() => {
    (async () => {
      try {
        if (activeTab === 'Contacts') {
          if (contacts === null) await loadContacts();
          if (calls === null) await loadCalls();
          if (messages === null) await loadMessages();
          // Always refresh emails on entry (not just first visit) so a send from the
          // Documents/Pictures tab shows up here immediately.
          await loadEmails();
        }
        else if (activeTab === 'Time Entries' && timeEntries === null) await loadTimeEntries();
        else if (activeTab === 'Checklists' && checklists === null) await loadChecklists();
        else if (activeTab === 'Parcels' && parcels === null) await loadParcels();
        else if (activeTab === 'Portal' && portal === null) await loadPortal();
        else if (activeTab === 'Pictures' && photos === null) await loadPhotos();
        else if (activeTab === 'Proposals & Services') {
          if (workOrders === null) await loadWorkOrders();
          if (changeOrders === null && canManagePayroll) await loadChangeOrders();
        } else if ((activeTab === 'Profitability' || activeTab === 'Finances') && canManagePayroll) {
          if (profit === null) await loadProfit();
          if (expenses === null) await loadExpenses();
          if (activeTab === 'Finances') await loadJobInvoices();
        }
      } catch (e) {
        console.error('Error loading tab data:', e);
      }
    })();
  }, [activeTab, id, canManagePayroll]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusChange = async (newStatus) => {
    try {
      await updateJob(id, { status: newStatus });
      setJob(j => ({ ...j, status: newStatus }));
    } catch (e) {
      console.error('Error updating status:', e);
      alert('Could not update status');
    }
  };

  // ---- Job phases (multi-client: Design / Construction) ----
  const [phaseBusy, setPhaseBusy] = useState(false);
  const [editingPhase, setEditingPhase] = useState(null);   // phase object being edited (or {new:true})

  const enablePhases = async () => {
    if (!window.confirm('Split this job into billing phases (Design / Construction)?\n\nThe current client becomes the Design phase. You can then add a Construction phase with a different client. Nothing is lost.')) return;
    setPhaseBusy(true);
    try {
      await axios.post(`${API_URL}/jobs/${id}/enable-phases`, {}, await getAuthConfig());
      await loadJob();
    } catch (e) {
      alert(e.response?.data?.error || 'Could not enable phases.');
    }
    setPhaseBusy(false);
  };

  const savePhase = async (draft) => {
    setPhaseBusy(true);
    try {
      const body = {
        name: draft.name, kind: draft.kind,
        client_id: draft.client_id || null,
        contract_amount: draft.contract_amount === '' ? null : draft.contract_amount,
        status: draft.status,
      };
      if (draft.id) {
        await axios.patch(`${API_URL}/jobs/${id}/phases/${draft.id}`, body, await getAuthConfig());
      } else {
        await axios.post(`${API_URL}/jobs/${id}/phases`, body, await getAuthConfig());
      }
      setEditingPhase(null);
      await loadJob();
    } catch (e) {
      alert(e.response?.data?.error || 'Could not save the phase.');
    }
    setPhaseBusy(false);
  };

  const setEntryPhase = async (entry, phaseId) => {
    try {
      await axios.put(`${API_URL}/time-entries/${entry.id}`, { phase_id: phaseId || null }, await getAuthConfig());
      await loadTimeEntries();
    } catch (e) {
      alert(e.response?.data?.error || 'Could not set the phase for this entry.');
    }
  };

  const [enRoutePhaseId, setEnRoutePhaseId] = useState(null);
  const sendPhaseEnRoute = async (phase) => {
    if (!window.confirm(`Text & email everyone paying for the ${phase.name} phase that the crew is on the way?`)) return;
    setEnRoutePhaseId(phase.id);
    try {
      const res = await axios.post(`${API_URL}/jobs/${id}/portal/en-route`, { phase_id: phase.id }, await getAuthConfig());
      alert(res.data?.message || 'Notified.');
    } catch (e) {
      alert(e.response?.data?.error || 'Could not send the en-route notification.');
    } finally {
      setEnRoutePhaseId(null);
    }
  };

  const copyPhasePortalLink = async (phase) => {
    try {
      const res = await axios.get(`${API_URL}/jobs/${id}/phases/${phase.id}/portal-link`, await getAuthConfig());
      const url = res.data?.url;
      if (!url) return;
      try { await navigator.clipboard.writeText(url); alert(`Portal link for ${phase.name} copied:\n\n${url}`); }
      catch (e) { window.prompt(`Portal link for ${phase.name}:`, url); }
    } catch (e) {
      alert(e.response?.data?.error || 'Could not get the phase portal link.');
    }
  };

  const setPhaseStatus = async (phase, status) => {
    try {
      await axios.patch(`${API_URL}/jobs/${id}/phases/${phase.id}`, { status }, await getAuthConfig());
      await loadJob();
    } catch (e) {
      alert(e.response?.data?.error || 'Could not update the phase status.');
    }
  };

  const removePhase = async (phase) => {
    if (!window.confirm(`Remove the ${phase.name || phase.kind} phase?\n\nAny invoices billed to it are kept — they just stop being tied to this phase.`)) return;
    setPhaseBusy(true);
    try {
      await axios.delete(`${API_URL}/jobs/${id}/phases/${phase.id}`, await getAuthConfig());
      await loadJob();
    } catch (e) {
      alert(e.response?.data?.error || 'Could not remove the phase.');
    }
    setPhaseBusy(false);
  };

  const startEditNumber = () => { setNumberDraft(job.job_number || ''); setEditingNumber(true); };
  const saveJobNumber = async () => {
    const v = numberDraft.trim();
    if (!v) { alert('Job number cannot be empty.'); return; }
    if (v === job.job_number) { setEditingNumber(false); return; }
    setSavingNumber(true);
    try {
      await updateJob(id, { job_number: v });
      setJob(j => ({ ...j, job_number: v }));
      setEditingNumber(false);
    } catch (e) {
      alert(e.response?.data?.error || 'Could not update the job number.');
    }
    setSavingNumber(false);
  };

  const [showReactivate, setShowReactivate] = useState(false);
  const [reactivateDate, setReactivateDate] = useState('');
  const [reactivateTime, setReactivateTime] = useState('');
  const [reactivating, setReactivating] = useState(false);

  const handleReactivate = async () => {
    if (!reactivateDate) return;
    setReactivating(true);
    try {
      await axios.post(
        `${API_URL}/jobs/${id}/reactivate`,
        { scheduled_date: reactivateDate, scheduled_time: reactivateTime || undefined },
        await getAuthConfig()
      );
      setShowReactivate(false);
      setReactivateDate('');
      setReactivateTime('');
      await loadJob();
    } catch (e) {
      alert(e.response?.data?.error || 'Could not reactivate the job.');
    }
    setReactivating(false);
  };

  // ----- Cancel job (ported from classic) -----
  const handleCancelJob = async () => {
    if (!cancelReason.trim()) { alert('A cancellation reason is required.'); return; }
    setCancelling(true);
    try {
      await axios.post(`${API_URL}/jobs/${id}/cancel`,
        { reason: cancelReason.trim(), notes: cancelNotes.trim() || undefined }, await getAuthConfig());
      setShowCancel(false); setCancelReason(''); setCancelNotes('');
      await loadJob();
    } catch (e) { alert(e.response?.data?.error || 'Could not cancel the job.'); }
    setCancelling(false);
  };

  // ----- Parcel image (for the printed Work Order) -----
  const uploadParcelImage = async (file) => {
    if (!file) return;
    setUploadingParcel(true);
    try {
      const fd = new FormData(); fd.append('image', file);
      await axios.post(`${API_URL}/jobs/${id}/parcel-image`, fd, await getAuthConfig());
      await loadJob();
    } catch (e) { alert(e.response?.data?.error || 'Could not upload the parcel image.'); }
    setUploadingParcel(false);
  };
  const deleteParcelImage = async () => {
    if (!window.confirm('Remove the parcel image?')) return;
    try { await axios.delete(`${API_URL}/jobs/${id}/parcel-image`, await getAuthConfig()); await loadJob(); }
    catch (e) { alert('Could not remove the parcel image.'); }
  };

  // ----- Job expenses (feed profitability) -----
  const loadExpenses = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/jobs/${id}/expenses`, await getAuthConfig()); setExpenses(res.data); }
    catch (e) { /* non-fatal — payroll-only */ }
  }, [id]);
  const saveExpenses = async () => {
    if (!expenses) return;
    setSavingExpenses(true);
    try {
      await axios.post(`${API_URL}/jobs/${id}/expenses`, {
        rebar_count: Number(expenses.rebar_count) || 0,
        pipe_count: Number(expenses.pipe_count) || 0,
        num_trips: Number(expenses.num_trips) || 0,
        crew_size: Number(expenses.crew_size) || 0,
        other_expenses: Number(expenses.other_expenses) || 0,
        other_expenses_note: expenses.other_expenses_note || '',
      }, await getAuthConfig());
      await loadProfit();
    } catch (e) { alert(e.response?.data?.error || 'Could not save expenses.'); }
    setSavingExpenses(false);
  };

  // ----- Whole-job en route (non-phased jobs) -----
  const sendWholeJobEnRoute = async () => {
    if (!window.confirm('Text & email the client that the crew is on the way?')) return;
    setEnRouteBusy(true);
    try {
      const res = await axios.post(`${API_URL}/jobs/${id}/portal/en-route`, {}, await getAuthConfig());
      alert(res.data?.message || 'Client notified.');
    } catch (e) { alert(e.response?.data?.error || 'Could not send the en-route notification.'); }
    setEnRouteBusy(false);
  };

  // ----- Bulk notify selected contacts -----
  const openNotify = async () => {
    if (contacts === null) await loadContacts();
    setNotifySel((contacts || []).map((_, i) => i));
    setNotifyTitle(''); setNotifyMsg(''); setNotifySms(true); setNotifyEmail(true);
    setShowNotify(true);
  };
  const toggleNotifySel = (i) => setNotifySel(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  const sendNotify = async () => {
    if (!notifyTitle.trim() && !notifyMsg.trim()) { alert('Enter a title or a message.'); return; }
    if (!notifySel.length) { alert('Select at least one contact.'); return; }
    if (!notifySms && !notifyEmail) { alert('Choose text, email, or both.'); return; }
    setNotifyBusy(true);
    try {
      const res = await axios.post(`${API_URL}/jobs/${id}/portal/notify-contacts`, {
        contact_indices: notifySel, title: notifyTitle.trim(), message: notifyMsg.trim(),
        send_sms: notifySms, send_email: notifyEmail,
      }, await getAuthConfig());
      alert(res.data?.message || 'Sent.');
      setShowNotify(false);
    } catch (e) { alert(e.response?.data?.error || 'Could not send.'); }
    setNotifyBusy(false);
  };

  // ----- Portal link quick actions -----
  const copyPortalLink = async () => {
    const url = `${window.location.origin}/portal/${portal?.token}`;
    try { await navigator.clipboard.writeText(url); alert('Portal link copied.'); }
    catch (e) { window.prompt('Portal link:', url); }
  };
  const textPortalLink = () => {
    const url = `${window.location.origin}/portal/${portal?.token}`;
    const phone = job?.client_phone;
    if (!phone) { alert('No phone number on file for the client.'); return; }
    const msg = `North Georgia Surveying, Inc.\n\nTrack your project here: ${url}\n\nQuestions? Call us at 706-625-1046`;
    window.open(`sms:${phone.replace(/[^\d+]/g, '')}?body=${encodeURIComponent(msg)}`, '_blank');
  };

  const [formModal, setFormModal] = useState(null); // generic create/edit modal config

  // ----- Work Orders -----
  const WO_STATUSES = ['Pending', 'Scheduled', 'In Progress', 'On Hold', 'Completed', 'Invoiced', 'Paid'];
  const setWoStatus = async (wo, status) => {
    try {
      await axios.put(`${API_URL}/jobs/${id}/work-orders/${wo.id}`, { status }, await getAuthConfig());
      await loadWorkOrders();
    } catch (e) { alert(e.response?.data?.error || 'Could not update the work order.'); }
  };
  // Take a work order off hold. If its original date already passed, open the form on
  // today's date so it can't get stranded off the schedule; otherwise resume in place.
  const resumeWorkOrder = (wo) => {
    const d = wo.scheduled_date;  // 'YYYY-MM-DD' or null
    const t = new Date();
    const todayStr = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    if (d && d < todayStr) {
      alert(`This was scheduled for ${d}, which has already passed. Pick a new date to put it back on the schedule.`);
      openWorkOrderForm(wo, { status: 'Scheduled', scheduled_date: todayStr });
      return;
    }
    setWoStatus(wo, d ? 'Scheduled' : 'Pending');  // future date → back on the board; no date → Pending
  };
  const openWorkOrderForm = (wo, overrides = {}) => {
    const fields = [
      { key: 'description', label: 'Description', type: 'textarea', required: true },
      { key: 'po_number', label: 'PO Number', type: 'text' },
      { key: 'status', label: 'Status', type: 'select', options: WO_STATUSES },
      { key: 'included_in_contract', label: 'Part of base contract (no separate price)', type: 'checkbox' },
      { key: 'scheduled_date', label: 'Scheduled Date', type: 'date' },
      { key: 'completed_date', label: 'Completed Date', type: 'date' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ];
    if (canManagePayroll) {
      fields.splice(2, 0, { key: 'quoted_amount', label: 'Quoted Amount ($)', type: 'number' });
    }
    // Phased job: let the work order belong to a phase (bills that phase's client).
    if (job.has_phases) {
      fields.splice(1, 0, {
        key: 'phase_id', label: 'Phase', type: 'select',
        options: [{ value: '', label: '— No specific phase —' },
          ...(job.phases || []).map(p => ({ value: p.id, label: p.name }))],
      });
    }
    setFormModal({
      title: wo ? `Edit ${wo.order_number}` : 'New Work Order',
      fields,
      initial: { ...(wo || { status: 'Pending' }), ...overrides },
      submitLabel: wo ? 'Save' : 'Create',
      onSubmit: async (vals) => {
        const cfg = await getAuthConfig();
        if (wo) await axios.put(`${API_URL}/jobs/${id}/work-orders/${wo.id}`, vals, cfg);
        else await axios.post(`${API_URL}/jobs/${id}/work-orders`, vals, cfg);
        await loadWorkOrders();
        loadJob(); // multi-phase flag may have changed
      },
    });
  };
  const deleteWorkOrder = async (wo) => {
    if (!window.confirm(`Delete ${wo.order_number}?`)) return;
    try {
      await axios.delete(`${API_URL}/jobs/${id}/work-orders/${wo.id}`, await getAuthConfig());
      await loadWorkOrders();
    } catch (e) { alert(e?.response?.data?.error || 'Could not delete'); }
  };

  // ----- Change Orders (payroll only) -----
  const CO_STATUSES = ['Draft', 'Pending', 'Approved', 'Rejected'];
  const openChangeOrderForm = (co) => {
    setFormModal({
      title: co ? `Edit ${co.co_number}` : 'New Change Order',
      fields: [
        { key: 'title', label: 'Title', type: 'text', required: true },
        { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'reason', label: 'Reason', type: 'text' },
        { key: 'amount', label: 'Amount ($)', type: 'number' },
        { key: 'status', label: 'Status', type: 'select', options: CO_STATUSES },
        { key: 'requested_by', label: 'Requested By', type: 'text' },
      ],
      initial: co || { status: 'Draft' },
      submitLabel: co ? 'Save' : 'Create',
      onSubmit: async (vals) => {
        if (co) await updateChangeOrder(id, co.id, vals);
        else await createChangeOrder(id, vals);
        await loadChangeOrders();
        loadJob();
      },
    });
  };
  const removeChangeOrder = async (co) => {
    if (!window.confirm(`Delete ${co.co_number}?`)) return;
    try { await deleteChangeOrder(id, co.id); await loadChangeOrders(); loadJob(); }
    catch (e) { alert(e?.response?.data?.error || 'Could not delete'); }
  };
  const openChangeOrderPdf = async (co) => {
    const token = await getAuthToken();
    window.open(`${API_URL}/jobs/${id}/change-orders/${co.id}/pdf?token=${token || ''}`, '_blank');
  };
  const emailChangeOrder = async (co) => {
    const defaultEmail = job?.billing_email || job?.client_email || '';
    const to = window.prompt('Email the change order approval link to:', defaultEmail);
    if (!to) return;
    const message = window.prompt('Optional message to include (leave blank to skip):', '') || '';
    try {
      const res = await axios.post(
        `${API_URL}/jobs/${id}/change-orders/${co.id}/email`,
        { to_email: to.trim(), message },
        await getAuthConfig()
      );
      alert(res.data?.message || `Change order emailed to ${to}`);
      await loadChangeOrders();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to email change order');
    }
  };
  // Opens the device's native Messages app pre-filled with the approval link.
  // No backend / no Twilio — message comes from the user's own phone number.
  const isPhone = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  const textChangeOrder = async (co) => {
    if (!isPhone) {
      alert('Texting from the app is only available on a phone. Use the Email button from the computer, or text the link manually:\nhttps://app.northgeorgiasurveying.com/change-order/<token>');
      return;
    }
    // Ensure the share_token exists by hitting the email endpoint? Cheaper: use the existing share-token route.
    let token = co.share_token;
    if (!token) {
      try {
        const r = await axios.post(`${API_URL}/jobs/${id}/change-orders/${co.id}/share-token`, {}, await getAuthConfig());
        token = r.data?.share_token;
      } catch (e) { alert('Could not generate share link'); return; }
    }
    const url = `https://app.northgeorgiasurveying.com/change-order/${token}`;
    const phone = window.prompt('Text approval link to (phone number):', job?.billing_phone || job?.client_phone || '');
    if (!phone) return;
    const body = `Change Order ${co.co_number} for job ${job?.job_number || ''} is ready for your approval: ${url}\n— North Georgia Surveying`;
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    // sms: URI — works on both iOS (use & separator) and Android (use ? separator); body= works on both
    window.location.href = `sms:${cleanPhone}?body=${encodeURIComponent(body)}`;
  };

  // ----- Contacts -----
  const openContactForm = (c) => {
    const isPrimary = !!(c && c.is_primary);
    // Phase selector options (phased jobs only): one per specific phase (by id) + whole-job.
    // Keyed by phase id, not kind, so two 'construction' phases (e.g. Foundation vs Pad
    // Layout) billed to different clients each show and stay separate.
    const phaseOpts = [{ value: '', label: 'Whole job (all phases)' }];
    if (job?.has_phases) {
      (job.phases || []).forEach(p => {
        phaseOpts.push({ value: String(p.id), label: `${p.name || p.kind} phase` });
      });
    }
    const phaseField = (job?.has_phases && !isPrimary) ? [{
      key: 'phase_tag', label: 'Which phase is this contact for?', type: 'select',
      options: phaseOpts,
      hint: 'Design contacts see only the design portal and its updates; construction only theirs. Whole-job contacts get everything.',
    }] : [];
    setFormModal({
      title: isPrimary ? 'Edit Primary Client' : (c ? 'Edit Contact' : 'New Contact'),
      fields: isPrimary ? [
        { key: 'name', label: 'Name', type: 'text', required: true, hint: 'Updates the client record (applies to all of their jobs).' },
        { key: 'phone', label: 'Phone', type: 'text' },
        { key: 'email', label: 'Email', type: 'text' },
      ] : [
        { key: 'name', label: 'Name', type: 'text', required: true },
        { key: 'role', label: 'Role', type: 'text', placeholder: 'Site Foreman, Engineer, Architect, etc.' },
        { key: 'phone', label: 'Phone', type: 'text' },
        { key: 'email', label: 'Email', type: 'text' },
        { key: 'notify_sms', label: 'Notify by text', type: 'checkbox' },
        { key: 'notify_email', label: 'Notify by email', type: 'checkbox' },
        {
          key: 'is_subcontractor', label: 'Subcontractor / collaborator on this job', type: 'checkbox',
          hint: 'Engineers, architects, and other outside pros working with us on this project.',
        },
        {
          key: 'client_id', label: 'Link to existing client (grants portal access)', type: 'autocomplete',
          placeholder: 'Type a client name to search…',
          options: [...allClients.map(cl => ({ value: cl.id, label: cl.company_name ? `${cl.company_name} (${cl.name})` : cl.name }))],
          hint: 'If they are already a client, this job appears on their existing portal link. Picking one autofills their name, phone, and email below. Clear the box to unlink.',
          // Autofill contact details from the linked client's record.
          onChange: (clientId) => {
            if (!clientId) return {};
            const cl = allClients.find(c => String(c.id) === String(clientId));
            if (!cl) return {};
            const patch = {};
            if (cl.name) patch.name = cl.name;
            if (cl.phone) patch.phone = cl.phone;
            if (cl.email) patch.email = cl.email;
            return patch;
          },
        },
        ...phaseField,
      ],
      initial: c
        ? { ...c, phase_tag: (c.phase_tag && c.phase_tag !== 'whole') ? String(c.phase_tag) : '' }
        : { notify_sms: true, notify_email: true, is_subcontractor: false, client_id: '', phase_tag: '' },
      submitLabel: c ? 'Save' : 'Add Contact',
      onSubmit: async (vals) => {
        const cfg = await getAuthConfig();
        if (isPrimary) {
          await axios.put(`${API_URL}/clients/${job.client_id}`, { name: vals.name, phone: vals.phone, email: vals.email }, cfg);
          await loadJob();
        } else if (c) {
          await axios.put(`${API_URL}/jobs/${id}/contacts/${c.id}`, vals, cfg);
        } else {
          await axios.post(`${API_URL}/jobs/${id}/contacts`, vals, cfg);
        }
        await loadContacts();
      },
    });
  };
  const deleteContact = async (c) => {
    if (!window.confirm(`Remove ${c.name}?`)) return;
    try { await axios.delete(`${API_URL}/jobs/${id}/contacts/${c.id}`, await getAuthConfig()); await loadContacts(); }
    catch (e) { alert(e?.response?.data?.error || 'Could not delete'); }
  };

  // ----- Create a quote / invoice pre-filled from this job -----
  // Both endpoints create a real record immediately, so `billing` guards against a
  // double-click quietly producing two drafts with two sequence numbers burned.
  const [billingBusy, setBillingBusy] = useState('');

  const createFromJob = async (kind) => {
    const label = kind === 'quote' ? 'quote' : 'invoice';
    if (billingBusy) return;
    if (!window.confirm(
      `Create a new ${label} from job ${job?.job_number || ''}?\n\n`
      + `It will be pre-filled with this job's billing details and description, `
      + `saved as a draft, and opened for you to add pricing.`
    )) return;
    setBillingBusy(kind);
    try {
      const path = kind === 'quote' ? 'quotes' : 'invoices';
      const res = await axios.post(`${API_URL}/${path}/from-job/${id}`, {}, await getAuthConfig());
      const newId = res.data?.id;
      if (!newId) throw new Error('no id returned');
      navigate(`/billing/${path}/${newId}/edit`);
    } catch (e) {
      alert(e?.response?.data?.error || `Could not create the ${label} from this job.`);
    } finally {
      setBillingBusy('');
    }
  };

  // Pull people off the client company's saved roster instead of retyping them. Nobody is
  // pre-ticked: adding a contact starts sending them job notifications, so it's deliberate.
  const openRoster = async () => {
    setRosterOpen(true);
    setRosterBusy(true);
    setRosterPicked([]);
    try {
      const res = await axios.get(`${API_URL}/jobs/${id}/roster-candidates`, await getAuthConfig());
      setRoster(res.data || { client_label: '', candidates: [] });
    } catch (e) {
      setRoster({ client_label: '', candidates: [] });
    } finally {
      setRosterBusy(false);
    }
  };

  const toggleRosterPick = (cid) => {
    setRosterPicked(prev => prev.includes(cid) ? prev.filter(x => x !== cid) : [...prev, cid]);
  };

  const addFromRoster = async () => {
    if (rosterPicked.length === 0) return;
    setRosterBusy(true);
    try {
      await axios.post(`${API_URL}/jobs/${id}/contacts/from-roster`,
        { client_contact_ids: rosterPicked }, await getAuthConfig());
      setRosterOpen(false);
      setRosterPicked([]);
      await loadContacts();
    } catch (e) {
      alert(e?.response?.data?.error || 'Could not add contacts from the company list');
    } finally {
      setRosterBusy(false);
    }
  };

  // ----- Photo markup -----
  // Opens over the ORIGINAL photo so repeated edits never bake markup onto markup.
  const [markupPhoto, setMarkupPhoto] = useState(null);
  const [markupUrl, setMarkupUrl] = useState(null);
  const [bmpSymbols, setBmpSymbols] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } };
        const res = await axios.get(`${API_URL}/bmp-symbols`, cfg);
        if (!cancelled) setBmpSymbols(res.data || []);
      } catch (err) {
        console.warn('Could not load BMP symbols', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openPhotoMarkup = async (photo) => {
    try {
      const cfg = {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        responseType: 'blob',
      };
      const res = await axios.get(`${API_URL}/jobs/${photo.job_id}/photos/${photo.id}/original`, cfg);
      setMarkupUrl(URL.createObjectURL(res.data));
      setMarkupPhoto(photo);
    } catch (err) {
      alert('Could not open the photo: ' + (err.response?.data?.error || err.message));
    }
  };

  const savePhotoMarkup = async ({ json, dataUrl, hasMarkup }) => {
    const cfg = { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } };
    await axios.post(
      `${API_URL}/jobs/${markupPhoto.job_id}/photos/${markupPhoto.id}/markup`,
      hasMarkup ? { image_data: dataUrl, annotation_json: json } : { clear: true },
      cfg,
    );
    closePhotoMarkup();
    await loadPhotos();
  };

  const closePhotoMarkup = () => {
    if (markupUrl) URL.revokeObjectURL(markupUrl);
    setMarkupPhoto(null);
    setMarkupUrl(null);
  };

  // ----- Pictures -----
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPhotoEmail, setShowPhotoEmail] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPhoto(true);
    const failures = [];
    let queuedCount = 0;
    try {
      const token = await getAuthToken();
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append('photo', file);
          await appendDeviceLocation(fd, file); // geotag fresh captures for corner-photo map
          const res = await axios.post(`${API_URL}/jobs/${id}/photos`, fd, {
            withCredentials: true,
            headers: { Authorization: token ? `Bearer ${token}` : undefined, 'Content-Type': 'multipart/form-data' },
          });
          // No signal — held in the offline outbox and uploaded automatically
          // later. It shows in the gallery meanwhile via useQueuedPhotos.
          if (res?.data?.__queued) queuedCount += 1;
        } catch (err) {
          failures.push(
            `${file.name}: ${err?.__offlineStorageFull ? err.message : err?.response?.data?.error || 'upload failed'}`
          );
        }
      }
      await loadPhotos();
      if (failures.length) alert(`${failures.length} of ${files.length} failed:\n${failures.join('\n')}`);
      else if (queuedCount) {
        alert(
          `${queuedCount} photo${queuedCount === 1 ? '' : 's'} saved on this device — ` +
          `${queuedCount === 1 ? 'it' : 'they'} will upload automatically when you have signal.`
        );
      }
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };
  // Saves a photo to disk. The blob URL must NOT be revoked synchronously after
  // click() — desktop Chrome/Edge fetch the blob asynchronously, so an immediate
  // revoke cancels the download and nothing lands in the Downloads folder (this is
  // why saving worked on the phone, where the fallback/long-press path was used).
  const downloadPhoto = async (p, { silent = false } = {}) => {
    const src = `${API_BASE_URL || ''}${p.url}`;
    const name = p.original_filename || `photo-${p.id}.jpg`;
    try {
      const res = await fetch(src, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      // Give the browser time to start reading the blob before tearing it down.
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 30000);
      return true;
    } catch (e) {
      // Fallback: open in a new tab so the photo can still be saved manually.
      // Suppressed during a bulk save, which would otherwise spray open tabs.
      if (!silent) window.open(src, '_blank', 'noopener');
      return false;
    }
  };
  // Saves every photo on the job. Sequential on purpose: the browser asks once to
  // allow multiple downloads, and firing them in parallel makes it drop some.
  const downloadAllPhotos = async () => {
    const list = viewablePhotos;
    if (!list.length || bulkDl) return;
    if (list.length > 5 && !window.confirm(
      `Download all ${list.length} photos? Your browser may ask permission to save multiple files.`
    )) return;
    let failed = 0;
    setBulkDl({ done: 0, total: list.length });
    for (let i = 0; i < list.length; i++) {
      const ok = await downloadPhoto(list[i], { silent: true });
      if (!ok) failed++;
      setBulkDl({ done: i + 1, total: list.length });
      // Small gap so the browser's download queue keeps up.
      await new Promise(r => setTimeout(r, 350));
    }
    setBulkDl(null);
    if (failed) alert(`${failed} of ${list.length} photo(s) could not be downloaded. Try those individually.`);
  };
  const deletePhoto = async (p) => {
    if (!window.confirm('Delete this photo?')) return;
    try { await axios.delete(`${API_URL}/jobs/${id}/photos/${p.id}`, await getAuthConfig()); await loadPhotos(); }
    catch (e) { alert('Could not delete'); }
  };
  const togglePortalPhoto = async (p) => {
    try {
      await axios.put(`${API_URL}/jobs/${id}/photos/${p.id}`, { show_on_portal: !p.show_on_portal }, await getAuthConfig());
      await loadPhotos();
    } catch (e) { alert('Could not update portal visibility'); }
  };
  // Opens a prefilled email draft (subject + friendly message + portal link). On mobile it
  // uses the native share sheet; on desktop it opens a Gmail compose draft. The link shows
  // as a URL (plain text can't carry custom anchor text) — for a branded HTML email with a
  // hyperlinked "Photos" button, use "Email to Client" (needs the email provider restored).
  const copyShareLink = async () => {
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    // Open the tab synchronously (before any await) so popup blockers don't kill it.
    const win = isMobile ? null : window.open('', '_blank');
    try {
      const res = await axios.get(`${API_URL}/jobs/${id}/portal`, await getAuthConfig());
      const ptoken = res.data?.token;
      if (!ptoken) { if (win) win.close(); alert('Could not get the portal link.'); return; }
      const url = `${window.location.origin}/portal/${ptoken}`;
      const addr = job.property_address ? ` at ${job.property_address}` : '';
      const subject = `Project Photos — ${job.job_number}`;
      const body =
        `Hi,\n\n` +
        `Here are the photos from your survey project${addr}. ` +
        `Click the link below to view them anytime:\n\n` +
        `${url}\n\n` +
        `Thank you,\nNorth Georgia Surveying, Inc.`;
      if (isMobile && navigator.share) {
        await navigator.share({ title: subject, text: body });
        return;
      }
      const gmail = `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      if (win) { win.location.href = gmail; } else { window.open(gmail, '_blank'); }
      try { await navigator.clipboard.writeText(url); } catch (e) { /* clipboard optional */ }
    } catch (err) {
      if (win) win.close();
      if (err.name !== 'AbortError') alert('Could not open the share draft.');
    }
  };

  // ----- Time Entries (admin/payroll only) -----
  // Stored times are UTC; convert to/from the browser's local time for the datetime-local input.
  const utcToLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const localInputToUtc = (val) => (val ? new Date(val).toISOString() : null);

  const openTimeEntryForm = (entry) => {
    const crew = (job.team_members || []);
    setFormModal({
      title: entry ? 'Edit Time Entry' : 'Add Manual Time Entry',
      fields: [
        ...(entry ? [] : [{ key: '_member_name', label: 'Team Member', type: 'select',
          options: crew.length ? crew.map(m => m.name) : ['(no assigned crew)'], required: true }]),
        { key: 'work_type', label: 'Work Type', type: 'select', options: WORK_TYPE_OPTIONS },
        { key: 'clock_in', label: 'Clock In', type: 'datetime-local' },
        { key: 'clock_out', label: 'Clock Out', type: 'datetime-local' },
        { key: 'notes', label: 'Notes', type: 'text' },
      ],
      initial: entry
        ? { work_type: entry.work_type || '', clock_in: utcToLocalInput(entry.clock_in), clock_out: utcToLocalInput(entry.clock_out), notes: entry.notes }
        : { work_type: 'Field Work' },
      submitLabel: entry ? 'Save' : 'Add Entry',
      onSubmit: async (vals) => {
        const cfg = await getAuthConfig();
        if (entry) {
          await axios.put(`${API_URL}/time-entries/${entry.id}`,
            { clock_in: localInputToUtc(vals.clock_in), clock_out: localInputToUtc(vals.clock_out), work_type: vals.work_type, notes: vals.notes }, cfg);
        } else {
          const member = crew.find(m => m.name === vals._member_name);
          if (!member) throw new Error('Pick a team member');
          await axios.post(`${API_URL}/time-entries/manual`, {
            team_member_id: member.id,
            job_id: Number(id),
            clock_in: localInputToUtc(vals.clock_in),
            clock_out: localInputToUtc(vals.clock_out),
            work_type: vals.work_type,
            notes: vals.notes || 'Manual entry',
          }, cfg);
        }
        await loadTimeEntries();
      },
    });
  };
  const deleteTimeEntry = async (entry) => {
    if (!window.confirm('Delete this time entry?')) return;
    try { await axios.delete(`${API_URL}/time-entries/${entry.id}`, await getAuthConfig()); await loadTimeEntries(); }
    catch (e) { alert(e?.response?.data?.error || 'Could not delete'); }
  };

  // ----- Field Checklists -----
  const createChecklist = async () => {
    const name = window.prompt('Checklist name (e.g., "Phase 2 - Foundation As-Built"):') || undefined;
    try {
      await axios.post(`${API_URL}/field-checklists/create`, { job_id: id, checklist_type: 'boundary', name }, await getAuthConfig());
      await loadChecklists();
    } catch (e) { alert('Failed to create checklist'); }
  };
  const deleteChecklist = async (fc) => {
    if (!window.confirm('Delete this checklist?')) return;
    try { await axios.delete(`${API_URL}/field-checklists/${fc.id}`, await getAuthConfig()); await loadChecklists(); }
    catch (e) { alert('Failed to delete'); }
  };

  // ----- Parcels -----
  const handleParcelsUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingParcels(true);
    try { await uploadParcelsJson(id, file); await loadParcels(); }
    catch (err) { alert(err?.response?.data?.error || 'Upload failed'); }
    setUploadingParcels(false);
    e.target.value = '';
  };
  const removeParcel = async (p) => {
    if (!window.confirm('Remove this parcel?')) return;
    try { await deleteJobParcel(id, p.id); await loadParcels(); } catch (e) { alert('Could not delete'); }
  };
  const clearParcels = async () => {
    if (!window.confirm('Remove ALL parcels from this job?')) return;
    try { await clearJobParcels(id); await loadParcels(); } catch (e) { alert('Could not clear'); }
  };

  // ----- Property Boundary -----
  const handleBoundaryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBoundary(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = await getAuthToken();
      await axios.post(`${API_URL}/jobs/${id}/boundary`, fd, {
        withCredentials: true,
        headers: { Authorization: token ? `Bearer ${token}` : undefined, 'Content-Type': 'multipart/form-data' },
      });
      await loadJob();
    } catch (err) { alert(err?.response?.data?.error || 'Boundary upload failed'); }
    setUploadingBoundary(false);
    e.target.value = '';
  };
  const deleteBoundary = async () => {
    if (!window.confirm('Remove the property boundary?')) return;
    try { await axios.delete(`${API_URL}/jobs/${id}/boundary`, await getAuthConfig()); await loadJob(); }
    catch (e) { alert('Could not delete boundary'); }
  };

  // ----- Client Portal -----
  const togglePortal = async () => {
    try { await axios.post(`${API_URL}/jobs/${id}/portal/toggle`, {}, await getAuthConfig()); await loadPortal(); }
    catch (e) { alert('Could not toggle portal'); }
  };
  const emailPortalLink = async () => {
    try {
      await axios.post(`${API_URL}/jobs/${id}/portal/email-link`, {}, await getAuthConfig());
      alert('Portal link emailed to the client.');
    } catch (e) { alert(e?.response?.data?.error || 'Could not email link'); }
  };
  const toggleDigest = async () => {
    try {
      await axios.patch(`${API_URL}/jobs/${id}/portal/digest-settings`, { weekly_digest_enabled: !portal?.weekly_digest_enabled }, await getAuthConfig());
      await loadPortal();
    } catch (e) { alert('Could not update digest setting'); }
  };
  const openPortalMessage = () => setFormModal({
    title: 'Message Client',
    fields: [
      { key: 'title', label: 'Subject', type: 'text', required: true },
      { key: 'message', label: 'Message', type: 'textarea' },
    ],
    initial: {},
    submitLabel: 'Send',
    onSubmit: async (vals) => {
      await axios.post(`${API_URL}/jobs/${id}/portal/message`, { title: vals.title, message: vals.message }, await getAuthConfig());
      await loadPortal();
    },
  });

  if (loading) return <div className="loading">Loading job...</div>;
  if (!job) return <div className="error">Job not found</div>;

  const lat = job.latitude != null ? Number(job.latitude) : null;
  const lng = job.longitude != null ? Number(job.longitude) : null;
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const center = hasCoords ? [lat, lng] : null;
  const schedule = job.scheduled_date
    ? `${fmtDate(job.scheduled_date)}${job.scheduled_time ? ' · ' + fmtTime(job.scheduled_time) : ''}`
    : null;

  return (
    <div className="qv-page">
      <div className="qv-toolbar">
        <button className="btn btn-ghost" onClick={() => navigate(backPath)}>
          <FiArrowLeft /> Back to {backLabel}
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => setShowWorkOrder(true)}>
            <FiPrinter /> Print Work Order
          </button>
          {canManagePayroll && job.status !== 'Cancelled' && (
            <button className="btn btn-ghost" style={{ color: '#dc2626' }} onClick={() => { setCancelReason(''); setCancelNotes(''); setShowCancel(true); }}>
              <FiX /> Cancel Job
            </button>
          )}
          <Link to={`/jobs/${id}/classic`} className="btn btn-primary">
            <FiEdit /> Edit Details
          </Link>
        </div>
      </div>

      {/* Qfactor-style header */}
      <div className="qv-header">
        <div>
          {editingNumber ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
              <input
                value={numberDraft}
                onChange={(e) => setNumberDraft(e.target.value)}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') saveJobNumber(); if (e.key === 'Escape') setEditingNumber(false); }}
                style={{ fontSize: 22, fontWeight: 700, padding: '4px 10px', border: '1px solid #cbd5e1', borderRadius: 6, minWidth: 220 }}
              />
              <button className="btn btn-primary btn-sm" onClick={saveJobNumber} disabled={savingNumber || !numberDraft.trim()}>
                {savingNumber ? 'Saving…' : 'Save'}
              </button>
              <button className="btn btn-sm" onClick={() => setEditingNumber(false)} disabled={savingNumber}>Cancel</button>
            </div>
          ) : (
            <h1 className="qv-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{job.job_number}: {job.client_name}</span>
              <button title="Edit job number" onClick={startEditNumber}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2, display: 'inline-flex' }}>
                <FiEdit2 size={16} />
              </button>
            </h1>
          )}
          {job.property_address && (
            <div className="qv-subaddr"><strong>Property Address:</strong> {job.property_address}</div>
          )}
          {job.has_phases && (job.phases || []).length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              <button
                onClick={() => setActiveTab('Phases')}
                title="This job is split into phases billed to different clients — open the Phases tab"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#6d28d9', textTransform: 'uppercase', letterSpacing: '0.4px' }}
              >Phased:</button>
              {(job.phases || []).map(p => (
                <span key={p.id} style={{
                  fontSize: 11.5, padding: '2px 9px', borderRadius: 10, border: '1px solid',
                  background: p.kind === 'design' ? '#eef2ff' : p.kind === 'construction' ? '#ecfdf5' : '#f1f5f9',
                  borderColor: p.kind === 'design' ? '#c7d2fe' : p.kind === 'construction' ? '#a7f3d0' : '#e2e8f0',
                  color: p.kind === 'design' ? '#4338ca' : p.kind === 'construction' ? '#047857' : '#475569',
                }}>
                  <strong>{p.name || p.kind}</strong>{p.client_name ? ` → ${p.client_name}` : ''}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="qv-status-box">
          <span className="qv-status-caption">Status:</span>
          {job.status === 'Cancelled' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                padding: '5px 12px', borderRadius: 8, fontWeight: 700, fontSize: 13,
                background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb',
              }}>Cancelled</span>
              {canManagePayroll && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => { setReactivateDate(''); setReactivateTime(''); setShowReactivate(true); }}
                >
                  <FiRefreshCw /> Reactivate
                </button>
              )}
            </div>
          ) : (
            <select
              className="qv-status-select"
              value={job.status || ''}
              onChange={(e) => handleStatusChange(e.target.value)}
              style={{ borderColor: statusColor(job.status), color: statusColor(job.status) }}
            >
              {withCurrent(STATUSES, job.status).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>
      </div>

      {job.status === 'Cancelled' && job.cancelled_reason && (
        <div style={{
          margin: '0 0 12px', padding: '10px 14px', borderRadius: 10,
          background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 14,
        }}>
          <strong>Cancelled:</strong> {job.cancelled_reason}
        </div>
      )}

      {showReactivate && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }} onClick={() => !reactivating && setShowReactivate(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'white', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420,
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 18, color: '#111827' }}>Reactivate {job.job_number}</h2>
            <p style={{ margin: '0 0 16px', fontSize: 14, color: '#6b7280' }}>
              Pick the date to put this job back on the schedule. This also turns the client portal
              back on and restores the approved quote.
            </p>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Schedule date *
            </label>
            <input
              type="date" value={reactivateDate}
              onChange={(e) => setReactivateDate(e.target.value)}
              style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 15, boxSizing: 'border-box', marginBottom: 12 }}
            />
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Time (optional)
            </label>
            <input
              type="time" value={reactivateTime}
              onChange={(e) => setReactivateTime(e.target.value)}
              style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 15, boxSizing: 'border-box', marginBottom: 20 }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setShowReactivate(false)} disabled={reactivating}>Cancel</button>
              <button className="btn btn-primary" onClick={handleReactivate} disabled={!reactivateDate || reactivating}>
                {reactivating ? 'Reactivating…' : 'Reactivate & Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ margin: '12px 0' }}>
        <JobTimer jobId={job.id} jobNumber={job.job_number} jobStatus={job.status} phases={job.has_phases ? job.phases : null} onStatusChanged={() => loadJob()} />
      </div>

      {/* Sub-tab bar — hide financial tabs from users without billing access */}
      <div className="qv-tabs">
        {TABS.filter(t => canManagePayroll || (t !== 'Profitability' && t !== 'Finances' && t !== 'Phases')).map(tab => (
          <button
            key={tab}
            className={`qv-tab${activeTab === tab ? ' active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="qv-content">
        {activeTab === 'Details' && (
          <>
          <div className="qv-details-grid">
            {/* Map column */}
            <div className="qv-map-col">
              {hasCoords ? (
                <div className="qv-map">
                  <MapContainer center={center} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                    <BasemapLayers basemap={basemap} />
                    <Marker position={center} />
                  </MapContainer>
                  <BasemapToggle basemap={basemap} setBasemap={setBasemap} />
                </div>
              ) : (
                <div className="qv-map qv-map-empty" onClick={() => setShowLocationPicker(true)} style={{ cursor: 'pointer' }} title="Set location on a map">
                  <FiMapPin size={28} /><span>No location set — click to set on a map</span>
                </div>
              )}
              <div className="qv-coords" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span>{hasCoords ? `Coordinates (DD): ${lat.toFixed(6)}, ${lng.toFixed(6)}` : ''}</span>
                <button className="qv-edit-btn" title="Set location on a map" onClick={() => setShowLocationPicker(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <FiMapPin size={13} /> {hasCoords ? 'Edit on map' : 'Set on map'}
                </button>
              </div>
              <div className="qv-map-links">
                {job.property_address && (
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(job.property_address)}`} target="_blank" rel="noopener noreferrer"><FiMapPin size={12} /> Directions</a>
                )}
                {job.parcel_url && (
                  <a href={job.parcel_url} target="_blank" rel="noopener noreferrer"><FiExternalLink size={12} /> qPublic</a>
                )}
              </div>
              <div className="qv-group" style={{ marginTop: 14 }}>
                <div className="qv-group-title"><span>Parcel Image <span style={{ fontWeight: 400, color: '#94a3b8' }}>(for Work Order)</span></span></div>
                {job.parcel_image_url ? (
                  <div>
                    <img src={`${API_BASE_URL || ''}${job.parcel_image_url}`} alt="Parcel"
                      style={{ width: '100%', maxHeight: 200, objectFit: 'contain', border: '1px solid var(--bg-tertiary)', borderRadius: 8, background: '#fff' }} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <label className="btn btn-sm" style={{ cursor: uploadingParcel ? 'wait' : 'pointer' }}>
                        <FiUpload size={12} /> {uploadingParcel ? 'Uploading…' : 'Replace'}
                        <input type="file" accept="image/*" onChange={(e) => uploadParcelImage(e.target.files?.[0])} disabled={uploadingParcel} style={{ display: 'none' }} />
                      </label>
                      <button className="btn btn-sm" style={{ color: '#dc2626' }} onClick={deleteParcelImage}><FiTrash2 size={12} /> Remove</button>
                    </div>
                  </div>
                ) : (
                  <label className="btn btn-sm" style={{ cursor: uploadingParcel ? 'wait' : 'pointer' }}>
                    <FiUpload size={12} /> {uploadingParcel ? 'Uploading…' : 'Upload parcel image'}
                    <input type="file" accept="image/*" onChange={(e) => uploadParcelImage(e.target.files?.[0])} disabled={uploadingParcel} style={{ display: 'none' }} />
                  </label>
                )}
              </div>
              {isOffice && (
                <div className="qv-group" style={{ marginTop: 14 }}>
                  <div className="qv-group-title">
                    <span>Server Folders <span style={{ fontWeight: 400, color: '#94a3b8' }}>(office only)</span></span>
                    <button className="qv-edit-btn" title="Set folder paths" onClick={() => setEditSection(SECTION_FIELDS.serverFolders)}><FiEdit2 size={13} /></button>
                  </div>
                  {[
                    { label: 'Job Folder', path: job.job_folder_path },
                    { label: 'Property Records', path: job.property_records_path },
                  ].map(({ label, path }) => (
                    <div key={label} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
                      {path ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <a href={`ngsfolder:${encodeURIComponent(path)}`}
                            className="btn btn-sm" style={{ background: '#eef2ff', color: '#3730a3' }}
                            title={`Open ${path}`}>
                            <FiFolder size={12} /> Open
                          </a>
                          <button className="btn btn-sm" title="Copy path" onClick={() => copyServerPath(path)}><FiLink size={12} /> Copy</button>
                          <span style={{ fontSize: 11, color: '#94a3b8', wordBreak: 'break-all', flexBasis: '100%' }}>{path}</span>
                        </div>
                      ) : (
                        <button className="btn btn-sm" onClick={() => setEditSection(SECTION_FIELDS.serverFolders)}><FiPlus size={12} /> Set path</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Column 2 */}
            <div className="qv-col">
              <InfoGroup title="Site Access" onEdit={() => setEditSection(SECTION_FIELDS.siteAccess)} rows={[
                { label: 'On-Site Contact', value: job.location_person },
                { label: 'Access Code', value: job.access_code },
                { label: 'Flood Zone', value: job.flood_zone },
                { label: 'Access Notes', value: job.job_specific_notes },
              ]} />
              <InfoGroup title="Project Information" onEdit={() => setEditSection(SECTION_FIELDS.projectInfo)} rows={[
                { label: 'Type', value: job.service_type },
                { label: 'Description', value: job.description_of_work },
                { label: 'Purchase Order #', value: job.po_number },
                { label: 'Legal Description', value: job.legal_description },
                { label: 'Notes', value: job.notes },
              ]} />
              <InfoGroup title="Lot(s) / Tract" onEdit={() => setEditSection(SECTION_FIELDS.lotTract)} rows={[
                { label: 'Street Address', value: job.property_address },
                { label: 'City/State/Zip', value: job.location_city_state_zip },
                { label: 'Parcel', value: job.parcel },
                // A derived value is labelled with where it came from — a quote or
                // parcel figure is often the parent tract, so it must never read as
                // trustworthy as a hand-entered one.
                { label: 'Acres Surveyed', value: job.acres != null
                    ? `${job.acres}`
                    : (job.acres_effective != null
                        ? `${job.acres_effective} (from ${job.acres_source === 'quote' ? 'quote' : 'parcel records'} — verify)`
                        : null) },
                { label: 'Subdivision', value: job.subdivision },
                { label: 'County', value: job.county },
                { label: 'Land Lot', value: job.land_lot },
                { label: 'District', value: job.district },
                { label: 'Section', value: job.section },
                { label: 'GMD', value: job.gmd },
              ]} />
              {/* What the crew reported at close-out. Shown even when empty, so an
                  unanswered job is visibly unanswered and a bad answer can be fixed
                  here rather than in the database. */}
              <InfoGroup title="Field Conditions" onEdit={() => setEditSection(SECTION_FIELDS.conditions)} rows={[
                { label: 'Corners Set / Recovered', value: job.corners_count != null ? String(job.corners_count) : null },
                { label: 'Existing Monuments', value: optLabel(MONUMENT_OPTIONS, job.monument_recovery) },
                { label: 'Terrain & Vegetation', value: optLabel(SITE_OPTIONS, job.site_conditions) },
                { label: 'Reported', value: job.conditions_recorded_at
                    ? new Date(job.conditions_recorded_at + (job.conditions_recorded_at.endsWith('Z') ? '' : 'Z')).toLocaleDateString()
                    : null },
              ]} />
            </div>

            {/* Column 3 */}
            <div className="qv-col">
              <InfoGroup title="Project Tracking" onEdit={() => setEditSection(SECTION_FIELDS.tracking)} rows={[
                { label: 'Status', value: <span style={{ color: statusColor(job.status), fontWeight: 700 }}>{job.status}</span> },
                { label: 'Priority', value: job.priority },
                { label: 'Submittal Status', value: job.submittal_status },
                ...(canManagePayroll ? [
                  { label: 'Invoice Status', value: job.billing_invoice_status },
                  { label: 'Payment Status', value: (job.billing_payment_status && job.billing_payment_status !== '—') ? job.billing_payment_status : null },
                ] : []),
              ]} />
              <InfoGroup title="Project Team" rows={
                (job.team_members && job.team_members.length)
                  ? job.team_members.map((m, i) => ({ label: i === 0 ? 'Assigned' : '', value: `${m.name}${m.role ? ' — ' + m.role : ''}` }))
                  : [{ label: 'Assigned', value: null }]
              } />
              {user?.id && job.status !== 'Cancelled' && (
                <div style={{ margin: '-4px 0 10px' }}>
                  <SelfAssignButton job={job} teamMemberId={user.id} onAssigned={loadJob} />
                </div>
              )}
              <InfoGroup title="Primary Contact" rows={[
                { label: 'Client', value: job.client_name },
                { label: 'Phone', value: job.client_phone ? <a href={`tel:${job.client_phone}`}><FiPhone size={11} /> {job.client_phone}</a> : null },
                { label: 'Email', value: job.client_email ? <a href={`mailto:${job.client_email}`}><FiMail size={11} /> {job.client_email}</a> : null },
              ]} />
              <InfoGroup title="Related Jobs" onEdit={() => setEditParent(true)} rows={[
                { label: 'Parent Job', value: job.parent_job_number ? <Link to={`/jobs/${job.parent_job_id}/v2`}>{job.parent_job_number}</Link> : null },
                { label: 'Child Jobs', value: (job.child_jobs && job.child_jobs.length) ? job.child_jobs.map(c => c.job_number).join(', ') : null },
                { label: 'Multi-Phase', value: job.is_multi_phase ? 'Yes — work orders enabled' : null },
                { label: 'Work Orders', value: job.work_orders_count ? String(job.work_orders_count) : null },
              ]} />
            </div>

            {/* Column 4 */}
            <div className="qv-col">
              {canManagePayroll && (
                <InfoGroup title="Estimated Fee" rows={[
                  { label: 'Contract', value: job.contract_amount ? fmtMoney(job.contract_amount) : null },
                  { label: 'Change Orders', value: job.change_orders_total ? fmtMoney(job.change_orders_total) : null },
                  { label: 'Total Value', value: job.total_contract_value ? fmtMoney(job.total_contract_value) : null },
                ]} />
              )}
              <InfoGroup title="Project Dates" onEdit={() => setEditSection(SECTION_FIELDS.dates)} rows={[
                { label: 'Work Ordered', value: fmtDate(job.work_ordered_date) },
                { label: 'Scheduled', value: schedule },
                { label: 'Started', value: fmtDate(job.work_started_date) },
                { label: 'Finished', value: fmtDate(job.work_finished_date) },
                { label: 'Completed', value: fmtDate(job.completion_date) },
              ]} />
            </div>

            {/* Column 5 */}
            <div className="qv-col">
              <InfoGroup title="Billing Company" rows={[
                { label: 'Company', value: job.billing_company },
                { label: 'Address', value: job.billing_address },
                { label: 'City/State/Zip', value: job.billing_city_state_zip },
              ]} />
              <InfoGroup title="Billing Contact" rows={[
                { label: 'Bill To', value: job.billing_person },
                { label: 'Phone', value: job.billing_phone },
                { label: 'Email', value: job.billing_email },
              ]} />
            </div>
          </div>
          </>
        )}

        {activeTab === 'Phases' && (
          <div className="dash-panel">
            <div className="qv-tab-headrow">
              <h3 className="qv-tabhead">Phases</h3>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 14px' }}>
              Use phases when one job is billed to different clients over its life — e.g. a
              Design phase for one client, then Construction for another. The job stays one
              record (same site, field work, and files); each phase bills its own client.
            </p>

            {!job.has_phases ? (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16 }}>
                <div style={{ fontSize: 14, color: '#334155', marginBottom: 10 }}>
                  This is a single-client job. Split it into phases to bill design and construction separately.
                </div>
                <button className="btn btn-primary btn-sm" onClick={enablePhases} disabled={phaseBusy}>
                  {phaseBusy ? 'Working…' : 'Split into phases'}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(job.phases || []).map(p => (
                  <div key={p.id} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
                        padding: '2px 8px', borderRadius: 6,
                        background: p.kind === 'design' ? '#eef2ff' : p.kind === 'construction' ? '#ecfdf5' : '#f1f5f9',
                        color: p.kind === 'design' ? '#4338ca' : p.kind === 'construction' ? '#047857' : '#475569',
                      }}>{p.kind}</span>
                      <strong style={{ fontSize: 15 }}>{p.name}</strong>
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="btn btn-sm" style={{ background: '#16a34a', color: '#fff' }} disabled={enRoutePhaseId === p.id} onClick={() => sendPhaseEnRoute(p)} title={`Text & email the ${p.name} phase's client/contacts that the crew is on the way`}>
                          🚗 {enRoutePhaseId === p.id ? 'Sending…' : 'En route'}
                        </button>
                        <button className="btn btn-sm" onClick={() => copyPhasePortalLink(p)} title="Copy this phase's client portal link"><FiLink size={12} /> Portal link</button>
                        <button className="btn btn-sm" onClick={() => setEditingPhase({ ...p })}><FiEdit2 size={12} /> Edit</button>
                        <button className="btn btn-sm" style={{ color: '#dc2626' }} onClick={() => removePhase(p)}><FiTrash2 size={12} /> Remove</button>
                      </span>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 13.5, color: '#334155', display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span><span style={{ color: '#94a3b8' }}>Client:</span> <strong>{p.client_name || '— none set —'}</strong></span>
                      {p.contract_amount != null && <span><span style={{ color: '#94a3b8' }}>Contract:</span> ${Number(p.contract_amount).toLocaleString()}</span>}
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: '#94a3b8' }}>Status:</span>
                        <select value={p.status || 'Pending'} onChange={(e) => setPhaseStatus(p, e.target.value)}
                          style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}>
                          {withCurrent(['Pending', ...STATUSES], p.status).filter((v, i, a) => a.indexOf(v) === i).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </span>
                    </div>
                  </div>
                ))}
                <div>
                  <button className="btn btn-primary btn-sm" onClick={() => setEditingPhase({ new: true, kind: 'construction', name: 'Construction', client_id: '', contract_amount: '', status: 'Pending' })} disabled={phaseBusy}>
                    <FiPlus size={13} /> Add phase
                  </button>
                </div>
              </div>
            )}

            {editingPhase && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => !phaseBusy && setEditingPhase(null)}>
                <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 14, padding: 22, width: '100%', maxWidth: 440 }}>
                  <h3 style={{ margin: '0 0 12px', fontSize: 17 }}>{editingPhase.id ? 'Edit phase' : 'Add phase'}</h3>
                  <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Type</label>
                  <select value={editingPhase.kind || 'construction'} onChange={(e) => setEditingPhase(p => ({ ...p, kind: e.target.value }))}
                    style={{ width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', marginBottom: 10, fontSize: 14 }}>
                    <option value="design">Design</option>
                    <option value="construction">Construction</option>
                    <option value="other">Other</option>
                  </select>
                  <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Name</label>
                  <input value={editingPhase.name || ''} onChange={(e) => setEditingPhase(p => ({ ...p, name: e.target.value }))}
                    style={{ width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', marginBottom: 10, fontSize: 14, boxSizing: 'border-box' }} />
                  <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Client (billed for this phase)</label>
                  <select value={editingPhase.client_id || ''} onChange={(e) => setEditingPhase(p => ({ ...p, client_id: e.target.value }))}
                    style={{ width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', marginBottom: 10, fontSize: 14 }}>
                    <option value="">— Select a client —</option>
                    {allClients.map(cl => <option key={cl.id} value={cl.id}>{cl.company_name ? `${cl.company_name} (${cl.name})` : cl.name}</option>)}
                  </select>
                  <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Contract amount (optional)</label>
                  <input type="number" value={editingPhase.contract_amount ?? ''} onChange={(e) => setEditingPhase(p => ({ ...p, contract_amount: e.target.value }))}
                    style={{ width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', marginBottom: 16, fontSize: 14, boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button className="btn btn-sm" onClick={() => setEditingPhase(null)} disabled={phaseBusy}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={() => savePhase(editingPhase)} disabled={phaseBusy}>{phaseBusy ? 'Saving…' : 'Save'}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'Proposals & Services' && (
          <div className="dash-panel">
            <div className="qv-tab-headrow">
              <h3 className="qv-tabhead">Work Orders</h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {canEditQuotes && (
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={billingBusy === 'quote'}
                    onClick={() => createFromJob('quote')}
                    title="Start a draft quote pre-filled from this job"
                  >
                    <FiFileText /> {billingBusy === 'quote' ? 'Creating…' : 'Create Quote'}
                  </button>
                )}
                <button className="btn btn-primary btn-sm" onClick={() => openWorkOrderForm(null)}><FiPlus /> New Work Order</button>
              </div>
            </div>
            {workOrders && workOrders.length > 0 ? (() => {
              const counts = workOrders.reduce((a, w) => {
                a[w.status] = (a[w.status] || 0) + 1; a.__all += 1;
                if (w.status !== 'Paid' && w.status !== 'On Hold') a.__open += 1; return a;
              }, { __all: 0, __open: 0 });
              const chipDefs = [
                { key: 'open', label: 'Open', count: counts.__open },
                { key: 'Pending', label: 'Pending', count: counts['Pending'] || 0 },
                { key: 'Scheduled', label: 'Scheduled', count: counts['Scheduled'] || 0 },
                { key: 'In Progress', label: 'In Progress', count: counts['In Progress'] || 0 },
                { key: 'On Hold', label: 'On Hold', count: counts['On Hold'] || 0 },
                { key: 'Completed', label: 'Completed', count: counts['Completed'] || 0 },
                { key: 'Invoiced', label: 'Invoiced', count: counts['Invoiced'] || 0 },
                { key: 'Paid', label: 'Paid', count: counts['Paid'] || 0 },
                { key: 'all', label: 'All', count: counts.__all },
              ].filter(c => c.key === 'open' || c.key === 'all' || c.count > 0);
              const match = (w) => woFilter === 'all' ? true : woFilter === 'open' ? (w.status !== 'Paid' && w.status !== 'On Hold') : w.status === woFilter;
              const visible = workOrders.filter(match);
              return (<>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                  {chipDefs.map(c => {
                    const active = woFilter === c.key;
                    return (
                      <button key={c.key} onClick={() => setWoFilter(c.key)}
                        style={{
                          padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          border: '1px solid ' + (active ? '#2563eb' : '#e2e8f0'),
                          background: active ? '#2563eb' : 'white', color: active ? 'white' : '#475569',
                        }}>
                        {c.label} <span style={{ opacity: 0.7 }}>{c.count}</span>
                      </button>
                    );
                  })}
                </div>
                {visible.length === 0 ? (
                  <div className="dash-mini-empty">No {woFilter === 'open' ? 'open' : woFilter === 'all' ? '' : `“${woFilter}”`} work orders. {woFilter !== 'all' && <button onClick={() => setWoFilter('all')} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 600, padding: 0 }}>Show all →</button>}</div>
                ) : (
              <table className="dash-prof-table">
                <thead><tr><th>Order</th><th>Description</th>{job.has_phases && <th>Phase</th>}{canManagePayroll && <th style={{ textAlign: 'right' }}>Quoted</th>}<th>Status</th><th style={{ width: 70 }}></th></tr></thead>
                <tbody>
                  {visible.map(wo => (
                    <tr key={wo.id}>
                      <td>{wo.order_number}</td>
                      <td>
                        {wo.description}
                        {wo.included_in_contract && (
                          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#0369a1', background: '#e0f2fe', padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>IN CONTRACT</span>
                        )}
                      </td>
                      {job.has_phases && (
                        <td>{wo.phase_name
                          ? <span style={{ fontSize: 11, fontWeight: 700, background: '#eef2ff', color: '#4338ca', padding: '2px 7px', borderRadius: 6 }}>{wo.phase_name}</span>
                          : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      )}
                      {canManagePayroll && <td style={{ textAlign: 'right' }}>{wo.included_in_contract ? <span style={{ color: '#64748b', fontSize: 12 }}>Included</span> : fmtMoney(wo.quoted_amount)}</td>}
                      <td>{wo.status === 'On Hold'
                        ? <span style={{ fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: 6 }}>⏸ On Hold</span>
                        : wo.status}</td>
                      <td className="qv-row-actions">
                        {wo.status === 'On Hold'
                          ? <button className="qv-edit-btn" title="Take off hold — if the date has passed you'll pick a new one" onClick={() => resumeWorkOrder(wo)} style={{ color: '#16a34a' }}>▶ Resume</button>
                          : <button className="qv-edit-btn" title="Put on hold — keeps its date but drops off the schedule board" onClick={() => setWoStatus(wo, 'On Hold')} style={{ color: '#b45309' }}>⏸ Hold</button>}
                        <button className="qv-edit-btn" title="Attachments (crew-only drawings/files)" onClick={() => setWoAttachments(wo)}><FiPaperclip size={13} /></button>
                        <button className="qv-edit-btn" title="Edit" onClick={() => openWorkOrderForm(wo)}><FiEdit2 size={13} /></button>
                        {canManagePayroll && <button className="qv-del-btn" title="Delete" onClick={() => deleteWorkOrder(wo)}><FiTrash2 size={13} /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
                )}
              </>);
            })() : <div className="dash-mini-empty">No work orders on this job</div>}

            {canManagePayroll && (
              <>
                <div className="qv-tab-headrow" style={{ marginTop: '1.5rem' }}>
                  <h3 className="qv-tabhead">Change Orders</h3>
                  <button className="btn btn-primary btn-sm" onClick={() => openChangeOrderForm(null)}><FiPlus /> New Change Order</button>
                </div>
                {changeOrders && changeOrders.length > 0 ? (
                  <table className="dash-prof-table">
                    <thead><tr><th>CO</th><th>Title</th><th style={{ textAlign: 'right' }}>Amount</th><th>Status</th><th style={{ width: 70 }}></th></tr></thead>
                    <tbody>
                      {changeOrders.map(co => (
                        <tr key={co.id}>
                          <td>{co.co_number}</td>
                          <td>{co.title || co.description}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoney(co.amount)}</td>
                          <td>
                            {co.status}
                            {co.client_signature && (
                              <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2, lineHeight: 1.3 }}>
                                ✍ Signed by <strong>{co.client_signature}</strong>
                                {co.client_signed_at && (
                                  <> on {new Date(co.client_signed_at + (co.client_signed_at.endsWith('Z') ? '' : 'Z')).toLocaleDateString()}</>
                                )}
                              </div>
                            )}
                            {co.declined_reason && (
                              <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 2, lineHeight: 1.3 }}>
                                Reason: {co.declined_reason}
                              </div>
                            )}
                            {co.email_status && co.email_status !== 'sent' && (() => {
                              const s = co.email_status;
                              const bad = ['bounce', 'dropped', 'blocked', 'spamreport'].includes(s);
                              const warn = s === 'deferred';
                              const good = ['delivered', 'open', 'click'].includes(s);
                              const color = bad ? '#b91c1c' : (warn ? '#b45309' : (good ? '#16a34a' : '#64748b'));
                              const icon = bad ? '❌' : (warn ? '⏳' : (good ? (s === 'open' ? '👀' : '✅') : '📨'));
                              const label = ({ delivered: 'Delivered', open: 'Opened', click: 'Link clicked', bounce: 'Bounced', dropped: 'Dropped', blocked: 'Blocked', spamreport: 'Marked spam', deferred: 'Deferred (retrying)' })[s] || s;
                              return (
                                <div style={{ fontSize: 11, color, marginTop: 2, lineHeight: 1.3 }} title={co.email_status_reason || ''}>
                                  {icon} {label}{co.email_status_reason && bad ? ` — ${co.email_status_reason.slice(0, 80)}` : ''}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="qv-row-actions">
                            <button className="qv-edit-btn" title="View PDF" onClick={() => openChangeOrderPdf(co)}><FiExternalLink size={13} /></button>
                            <button className="qv-edit-btn" title="Email approval link to client" onClick={() => emailChangeOrder(co)}><FiMail size={13} /></button>
                            <button className="qv-edit-btn" title={isPhone ? 'Text approval link from your phone' : 'Text (phone only — use Email on desktop)'} onClick={() => textChangeOrder(co)} style={!isPhone ? { opacity: 0.5 } : undefined}><FiPhone size={13} /></button>
                            <button className="qv-edit-btn" title="Edit" onClick={() => openChangeOrderForm(co)}><FiEdit2 size={13} /></button>
                            <button className="qv-del-btn" title="Delete" onClick={() => removeChangeOrder(co)}><FiTrash2 size={13} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <div className="dash-mini-empty">No change orders</div>}
                <div className="qv-tabfoot"><Link to="/billing">Manage quotes & invoices →</Link></div>
              </>
            )}
          </div>
        )}

        {activeTab === 'Contacts' && (
          <div className="dash-panel">
            <div className="qv-tab-headrow">
              <h3 className="qv-tabhead">Contacts</h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-secondary btn-sm" onClick={openRoster} title="Pick from the people saved on this client company">
                  <FiUsers /> Add from Company
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => openContactForm(null)}><FiPlus /> New Contact</button>
              </div>
            </div>

            {rosterOpen && (
              <div style={{
                margin: '0 0 16px', padding: 12, background: '#f8fafc',
                border: '1px solid #bfdbfe', borderRadius: 8,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <strong style={{ fontSize: 13 }}>
                    {roster.client_label ? `Contacts at ${roster.client_label}` : 'Company Contacts'}
                  </strong>
                  <button className="qv-del-btn" title="Close" onClick={() => setRosterOpen(false)}>
                    <FiX size={14} />
                  </button>
                </div>

                {rosterBusy && roster.candidates.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#64748b' }}>Loading…</div>
                ) : roster.candidates.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#64748b' }}>
                    No saved contacts for this client yet. Add them on the client's page and
                    they'll be there for every job.
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                      {roster.candidates.map(rc => {
                        const disabled = rc.already_added || rc.is_primary_client;
                        const why = rc.is_primary_client ? 'Already the primary client'
                          : rc.already_added ? 'Already on this job' : null;
                        const purposes = [
                          rc.receives_billing && 'Billing',
                          rc.receives_field && 'Field',
                          rc.receives_status && 'Status',
                        ].filter(Boolean);
                        return (
                          <label key={rc.id} style={{
                            display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px',
                            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                            cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
                          }}>
                            <input
                              type="checkbox"
                              disabled={disabled}
                              checked={rosterPicked.includes(rc.id)}
                              onChange={() => toggleRosterPick(rc.id)}
                              style={{ marginTop: 3 }}
                            />
                            <span style={{ minWidth: 0 }}>
                              <span style={{ fontSize: 13, fontWeight: 600 }}>{rc.name}</span>
                              {rc.title && <span style={{ fontSize: 12, color: '#64748b' }}> · {rc.title}</span>}
                              <span style={{ display: 'block', fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                {[rc.phone, rc.email].filter(Boolean).join('  ·  ')}
                              </span>
                              {(purposes.length > 0 || why) && (
                                <span style={{ display: 'flex', gap: 5, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                                  {purposes.map(p => (
                                    <span key={p} style={{
                                      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                                      letterSpacing: '0.4px', color: '#475569', background: '#f1f5f9',
                                      borderRadius: 999, padding: '2px 7px',
                                    }}>{p}</span>
                                  ))}
                                  {why && <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>{why}</span>}
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={rosterPicked.length === 0 || rosterBusy}
                      onClick={addFromRoster}
                    >
                      <FiPlus /> {rosterBusy ? 'Adding…'
                        : rosterPicked.length > 0 ? `Add ${rosterPicked.length} to This Job`
                          : 'Select someone to add'}
                    </button>
                  </>
                )}
              </div>
            )}
            {(() => {
              const list = contacts || [];
              const regular = list.filter(c => !c.is_subcontractor);
              const subs = list.filter(c => c.is_subcontractor);
              const card = (c) => (
                <div key={c.id || 'primary'} className="qv-contact-card">
                  <div className="qv-contact-name">
                    {c.name}{c.role ? <span className="qv-contact-role"> · {c.role}</span> : ''}
                    {job?.has_phases && (() => {
                      const cph = (job.phases || []).find(p => String(p.id) === String(c.phase_tag));
                      const kind = cph?.kind;
                      return (
                        <span style={{
                          marginLeft: 8, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
                          padding: '1px 7px', borderRadius: 6, verticalAlign: 'middle',
                          background: kind === 'design' ? '#eef2ff' : kind === 'construction' ? '#ecfdf5' : '#f1f5f9',
                          color: kind === 'design' ? '#4338ca' : kind === 'construction' ? '#047857' : '#64748b',
                        }}>{cph ? (cph.name || cph.kind) : 'All phases'}</span>
                      );
                    })()}
                    <span className="qv-contact-actions">
                      <button className="qv-edit-btn" title="Edit" onClick={() => openContactForm(c)}><FiEdit2 size={13} /></button>
                      {!c.is_primary && (
                        <button className="qv-del-btn" title="Remove" onClick={() => deleteContact(c)}><FiTrash2 size={13} /></button>
                      )}
                    </span>
                  </div>
                  {c.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <a href={`tel:${c.phone}`}><FiPhone size={12} /> {c.phone}</a>
                      <button
                        className="btn btn-sm"
                        style={{ background: '#16a34a', color: '#fff', padding: '2px 8px', fontSize: 12 }}
                        title="Call from the company number — rings your phone first, then connects you"
                        disabled={callingId === (c.id || 'primary')}
                        onClick={() => placeCall(c)}
                      >
                        <FiPhone size={11} /> {callingId === (c.id || 'primary') ? 'Calling…' : 'Call'}
                      </button>
                      <button
                        className="btn btn-sm"
                        style={{ background: '#2563eb', color: '#fff', padding: '2px 8px', fontSize: 12 }}
                        title="Text from the company number — your personal number stays hidden"
                        onClick={() => { setSmsTo(c); setSmsBody(''); }}
                      >
                        <FiMessageSquare size={11} /> Text
                      </button>
                    </div>
                  )}
                  {c.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <a href={`mailto:${c.email}`}><FiMail size={12} /> {c.email}</a>
                      <button
                        className="btn btn-sm"
                        style={{ background: '#0f766e', color: '#fff', padding: '2px 8px', fontSize: 12 }}
                        title="Email from the app — the reply comes back onto this job"
                        onClick={() => {
                          setEmailTo(c);
                          setEmailReplyToId(null);
                          setEmailSubject(`${job?.job_number || ''}${job?.property_address ? ' — ' + job.property_address : ''}`.trim());
                          setEmailBody(''); setEmailBodyHtml(''); setEmailFiles([]);
                        }}
                      >
                        <FiMail size={11} /> Email
                      </button>
                    </div>
                  )}
                  {c.linked_client_name && (
                    <div style={{ fontSize: 12, color: '#2563eb', marginTop: 4 }}>
                      ↳ linked to client: <strong>{c.linked_client_name}</strong> · portal access ✓
                    </div>
                  )}
                  {/* Explains why someone is here without anyone typing them in on this
                      job: they're saved on the client company's contact list. */}
                  {c.client_contact_id && (
                    <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 4 }}>
                      ↳ from the company contact list
                    </div>
                  )}
                  {c.notes && <div className="qv-contact-notes">{c.notes}</div>}
                </div>
              );
              return (
                <>
                  {regular.length > 0
                    ? <div className="qv-contacts">{regular.map(card)}</div>
                    : <div className="dash-mini-empty">No contacts yet.</div>}

                  {subs.length > 0 && (
                    <>
                      <div className="qv-tab-headrow" style={{ marginTop: 20 }}>
                        <h3 className="qv-tabhead">Subcontractors &amp; Collaborators</h3>
                      </div>
                      <div className="qv-contacts">{subs.map(card)}</div>
                    </>
                  )}
                </>
              );
            })()}

            <div className="qv-tab-headrow" style={{ marginTop: 24 }}>
              <h3 className="qv-tabhead">Call Log</h3>
            </div>
            {calls && calls.length > 0 ? (
              <table className="dash-prof-table">
                <thead><tr><th></th><th>Contact</th><th>By</th><th>When</th><th style={{ textAlign: 'right' }}>Duration</th><th>Status</th>{canViewRecordings && <th>Recording</th>}</tr></thead>
                <tbody>
                  {calls.map(cl => (
                    <tr key={cl.id}>
                      <td title={cl.direction}>{cl.direction === 'inbound' ? '↙ In' : '↗ Out'}</td>
                      <td>{cl.contact_name || cl.to_number || '—'}</td>
                      <td>{cl.caller_name || (cl.direction === 'inbound' ? 'Office' : '—')}</td>
                      <td>{cl.created_at ? new Date(cl.created_at + (cl.created_at.endsWith('Z') ? '' : 'Z')).toLocaleString() : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{cl.duration_seconds ? `${Math.floor(cl.duration_seconds / 60)}m ${cl.duration_seconds % 60}s` : '—'}</td>
                      <td>{cl.status === 'voicemail' ? '📨 Voicemail' : (cl.status || '—')}</td>
                      {canViewRecordings && (
                        <td>
                          {cl.has_recording
                            ? <audio controls preload="none" style={{ height: 30, maxWidth: 200 }} src={`${API_BASE_URL}${cl.recording_play_url}?token=${mediaToken}`} />
                            : '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="dash-mini-empty">No calls logged on this job yet.</div>}

            <div className="qv-tab-headrow" style={{ marginTop: 24 }}>
              <h3 className="qv-tabhead" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setShowTexts(v => !v)}>
                {showTexts ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                Text Messages
                {messages && messages.length > 0 && (
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', background: '#f1f5f9', borderRadius: 10, padding: '1px 8px' }}>{messages.length}</span>
                )}
              </h3>
            </div>

            {smsTo && (
              <div style={{ marginBottom: 12, padding: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                <div style={{ fontSize: 13, marginBottom: 6 }}>
                  Texting <strong>{smsTo.name}</strong> ({smsTo.phone}) — they'll see the company number.
                </div>
                <textarea
                  value={smsBody}
                  onChange={(e) => setSmsBody(e.target.value)}
                  placeholder="Type your message…"
                  rows={3}
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14, resize: 'vertical' }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={sendingSms || !smsBody.trim()}
                    onClick={sendSms}
                  >
                    <FiSend size={12} /> {sendingSms ? 'Sending…' : 'Send Text'}
                  </button>
                  <button className="btn btn-sm" onClick={() => { setSmsTo(null); setSmsBody(''); }}>Cancel</button>
                </div>
              </div>
            )}

            {showTexts && (messages && messages.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {messages.map(m => {
                  const out = m.direction === 'outbound';
                  return (
                    <div key={m.id} style={{ alignSelf: out ? 'flex-end' : 'flex-start', maxWidth: '75%' }}>
                      <div style={{
                        padding: '8px 12px', borderRadius: 12, fontSize: 14, whiteSpace: 'pre-wrap',
                        background: out ? '#2563eb' : '#f1f5f9', color: out ? '#fff' : '#0f172a',
                      }}>
                        {m.body}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, textAlign: out ? 'right' : 'left' }}>
                        {out ? `${m.sender_name || 'Sent'}${m.status === 'failed' ? ' · failed' : ''}` : (m.contact_name || 'Client')}
                        {m.created_at ? ` · ${new Date(m.created_at + (m.created_at.endsWith('Z') ? '' : 'Z')).toLocaleString()}` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <div className="dash-mini-empty">No texts on this job yet.</div>)}

            <div className="qv-tab-headrow" style={{ marginTop: 24 }}>
              <h3 className="qv-tabhead" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setShowEmailList(v => !v)}>
                {showEmailList ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                Email
                {emails && emails.length > 0 && (
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', background: '#f1f5f9', borderRadius: 10, padding: '1px 8px' }}>{emails.length}</span>
                )}
              </h3>
            </div>

            {emailTo && (
              <div style={{ marginBottom: 12, padding: 12, background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 8 }}>
                <div style={{ fontSize: 13, marginBottom: 6 }}>
                  Emailing <strong>{emailTo.name}</strong> ({emailTo.email}) — sent from your company address.
                </div>
                <input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Subject"
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14, marginBottom: 6, boxSizing: 'border-box' }}
                />
                <EmailEditor
                  valueHtml={emailBodyHtml}
                  onChange={(html, text) => { setEmailBodyHtml(html); setEmailBody(text); }}
                  placeholder="Type your message…"
                />
                {emailFiles.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {emailFiles.map((f, i) => (
                      <span key={i} style={{ fontSize: 12, background: '#eef2ff', color: '#3730a3', padding: '3px 8px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <FiPaperclip size={11} /> {f.name}
                        <button onClick={() => setEmailFiles(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 0, display: 'inline-flex' }}><FiX size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className="btn btn-primary btn-sm" disabled={sendingEmail || !emailBody.trim()} onClick={sendJobEmail}>
                    <FiSend size={12} /> {sendingEmail ? 'Sending…' : 'Send Email'}
                  </button>
                  <label className="btn btn-sm" style={{ cursor: 'pointer' }}>
                    <FiPaperclip size={12} /> Attach
                    <input type="file" multiple style={{ display: 'none' }}
                      onChange={(e) => { const picked = Array.from(e.target.files || []); setEmailFiles(prev => [...prev, ...picked]); e.target.value = ''; }} />
                  </label>
                  <button className="btn btn-sm" onClick={() => { setEmailTo(null); setEmailBody(''); setEmailBodyHtml(''); setEmailFiles([]); setEmailSubject(''); setEmailReplyToId(null); }}>Cancel</button>
                </div>
              </div>
            )}

            {showEmailList && (emails && emails.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {emails.map(em => {
                  const open = openEmailId === em.id;
                  const out = em.direction === 'out';
                  return (
                    <div key={em.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                      <button
                        onClick={() => setOpenEmailId(open ? null : em.id)}
                        style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%', textAlign: 'left', padding: '10px 12px', background: open ? '#f8fafc' : 'white', border: 'none', cursor: 'pointer' }}
                      >
                        <span style={{ fontSize: 11, fontWeight: 700, color: out ? '#0f766e' : '#475569', flexShrink: 0 }}>
                          {out ? '↗ SENT' : '↙ IN'}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontWeight: 600, fontSize: 13.5 }}>{em.subject || '(no subject)'}</span>
                          <span style={{ color: '#64748b', fontSize: 12.5 }}>
                            {' — '}{out ? `to ${em.to_addr}` : `from ${em.from_name || em.from_addr}`}
                          </span>
                        </span>
                        {em.attachments?.length > 0 && (
                          <span style={{ fontSize: 11, color: '#64748b', flexShrink: 0 }}>
                            <FiPaperclip size={11} /> {em.attachments.length}
                          </span>
                        )}
                        <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>
                          {em.received_at ? new Date(em.received_at + (em.received_at.endsWith('Z') ? '' : 'Z')).toLocaleDateString() : ''}
                        </span>
                      </button>
                      {open && (
                        <div style={{ padding: '10px 12px', borderTop: '1px solid #f1f5f9', background: '#fbfdff' }}>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>
                            {em.received_at ? new Date(em.received_at + (em.received_at.endsWith('Z') ? '' : 'Z')).toLocaleString() : ''}
                            {em.sent_by_name ? ` · sent by ${em.sent_by_name}` : ''}
                          </div>
                          <EmailBody text={em.body_text} html={em.body_html} style={{ fontSize: 13.5, color: '#0f172a' }} />
                          {em.direction === 'in' && em.from_addr && (
                            <button
                              className="btn btn-sm"
                              style={{ background: '#0f766e', color: '#fff', padding: '3px 10px', fontSize: 12, marginTop: 10 }}
                              onClick={() => {
                                const subj = (em.subject || '').trim();
                                const when = em.received_at ? new Date(em.received_at + (em.received_at.endsWith('Z') ? '' : 'Z')).toLocaleString() : '';
                                const quoted = (em.body_text || '').split('\n').map(l => '> ' + l).join('\n');
                                const who = em.from_name || em.from_addr;
                                setEmailTo({ name: who, email: em.from_addr });
                                setEmailSubject(/^re:/i.test(subj) ? subj : `Re: ${subj}`);
                                setEmailBody(`\n\nOn ${when}, ${who} wrote:\n${quoted}`);
                                setEmailBodyHtml(`<p></p><p>On ${escapeHtml(when)}, ${escapeHtml(who)} wrote:</p><blockquote>${plainToHtml(em.body_text || '')}</blockquote>`);
                                setEmailFiles([]);
                                setEmailReplyToId(em.id);   // thread it for the recipient
                                setOpenEmailId(null);
                              }}
                            >
                              <FiCornerUpLeft size={11} /> Reply
                            </button>
                          )}
                          <button
                            className="btn btn-sm"
                            style={{ background: 'white', color: '#334155', padding: '3px 10px', fontSize: 12, marginTop: 10, marginLeft: 6 }}
                            title="Filed to the wrong job? Move it to the correct one."
                            onClick={() => { setMoveEmailFor(moveEmailFor === em.id ? null : em.id); setMoveQuery(''); setMoveHits([]); }}
                          >
                            <FiFolder size={11} /> Move to another job
                          </button>
                          <button
                            className="btn btn-sm"
                            style={{ background: 'white', color: '#334155', padding: '3px 10px', fontSize: 12, marginTop: 10, marginLeft: 6 }}
                            title="Take it off this job — the email is kept"
                            onClick={() => unfileJobEmail(em)}
                          >
                            <FiSlash size={11} /> Remove from job
                          </button>
                          {em.mailbox_owner_id === user?.id && (
                            <button
                              className="btn btn-sm"
                              style={{ background: '#fee2e2', color: '#991b1b', padding: '3px 10px', fontSize: 12, marginTop: 10, marginLeft: 6 }}
                              title="Delete + move to Trash in your inbox"
                              onClick={() => deleteJobEmail(em)}
                            >
                              <FiTrash2 size={11} /> Delete
                            </button>
                          )}
                          {moveEmailFor === em.id && (
                            <div style={{ marginTop: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
                              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>
                                Move this email to another job — it comes off <strong>{job?.job_number}</strong>.
                              </div>
                              <div style={{ position: 'relative', marginBottom: 6 }}>
                                <FiSearch size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                <input
                                  autoFocus
                                  value={moveQuery}
                                  onChange={(e) => setMoveQuery(e.target.value)}
                                  placeholder="Search job number or address…"
                                  style={{ width: '100%', padding: '8px 28px 8px 28px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13.5, boxSizing: 'border-box' }}
                                />
                                <button onClick={closeMovePicker}
                                  style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}>
                                  <FiX size={13} />
                                </button>
                              </div>
                              {moveHits.length > 0 ? (
                                <div style={{ maxHeight: 190, overflowY: 'auto', background: 'white', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                                  {moveHits.filter(h => parseInt(String(h.link).split('/').pop(), 10) !== parseInt(id, 10)).map((h, i) => (
                                    <button key={i} onClick={() => moveJobEmail(em, h.link)}
                                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 8px', background: 'none', border: 'none', borderTop: i ? '1px solid #eef2f7' : 'none', cursor: 'pointer', fontSize: 13 }}>
                                      <strong>{h.title}</strong>
                                      {h.subtitle ? <span style={{ color: '#64748b' }}> — {h.subtitle}</span> : null}
                                    </button>
                                  ))}
                                </div>
                              ) : moveQuery.trim().length >= 2 ? (
                                <div style={{ fontSize: 12.5, color: '#94a3b8', padding: 4 }}>No jobs match “{moveQuery}”.</div>
                              ) : null}
                            </div>
                          )}
                          {em.attachments?.length > 0 && (
                            <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {em.attachments.map(a => (
                                <a key={a.id} href={`${API_BASE_URL}${a.download_url}?token=${mediaToken}`}
                                  style={{ fontSize: 12, background: '#eef2ff', color: '#3730a3', padding: '4px 8px', borderRadius: 6, textDecoration: 'none' }}>
                                  <FiPaperclip size={10} /> {a.name}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : <div className="dash-mini-empty">No emails on this job yet.</div>)}
          </div>
        )}

        {activeTab === 'Time Entries' && (
          <div className="dash-panel">
            <div className="qv-tab-headrow">
              <h3 className="qv-tabhead">Time Entries</h3>
              {canManagePayroll && (
                <button className="btn btn-primary btn-sm" onClick={() => openTimeEntryForm(null)}><FiPlus /> Add Manual Entry</button>
              )}
            </div>
            {job.has_phases && canManagePayroll && (
              <div style={{ fontSize: 12.5, color: '#4338ca', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
                This is a phased job — set which phase each time entry belongs to so it bills to the right client.
              </div>
            )}
            {timeEntries && timeEntries.length > 0 ? (
              <table className="dash-prof-table">
                <thead><tr><th>Member</th><th>Type</th>{job.has_phases && <th>Phase</th>}<th>Clock In</th><th>Clock Out</th><th style={{ textAlign: 'right' }}>Hours</th>{canManagePayroll && <th style={{ width: 70 }}></th>}</tr></thead>
                <tbody>
                  {timeEntries.map(e => (
                    <tr key={e.id}>
                      <td>{e.member_name || e.team_member_name}</td>
                      <td>{e.work_type || '—'}</td>
                      {job.has_phases && (
                        <td>
                          {canManagePayroll ? (
                            <select value={e.phase_id || ''} onChange={(ev) => setEntryPhase(e, ev.target.value)}
                              style={{ padding: '3px 6px', borderRadius: 6, border: `1px solid ${e.phase_id ? '#cbd5e1' : '#fca5a5'}`, fontSize: 12.5, background: e.phase_id ? 'white' : '#fef2f2' }}>
                              <option value="">— Unassigned —</option>
                              {(job.phases || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                          ) : (e.phase_name || '—')}
                        </td>
                      )}
                      <td>{e.clock_in ? new Date(e.clock_in + (e.clock_in.endsWith('Z') ? '' : 'Z')).toLocaleString() : '—'}</td>
                      <td>{e.clock_out ? new Date(e.clock_out + (e.clock_out.endsWith('Z') ? '' : 'Z')).toLocaleString() : 'Active'}</td>
                      <td style={{ textAlign: 'right' }}>{e.total_hours ?? '—'}</td>
                      {canManagePayroll && (
                        <td className="qv-row-actions">
                          <button className="qv-edit-btn" title="Edit" onClick={() => openTimeEntryForm(e)}><FiEdit2 size={13} /></button>
                          <button className="qv-del-btn" title="Delete" onClick={() => deleteTimeEntry(e)}><FiTrash2 size={13} /></button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="dash-mini-empty">No time logged on this job yet</div>}
            {!canManagePayroll && <div className="qv-tabfoot" style={{ color: '#9ca3af' }}>Only admins can add or edit time entries.</div>}
          </div>
        )}

        {activeTab === 'Checklists' && (
          <div className="dash-panel">
            <div className="qv-tab-headrow">
              <h3 className="qv-tabhead">Field Checklists</h3>
              <button className="btn btn-primary btn-sm" onClick={createChecklist}><FiPlus /> New Checklist</button>
            </div>
            {checklists && checklists.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {checklists.map(fc => {
                  const isActive = fc.is_active && !fc.all_complete;
                  return (
                    <div key={fc.id} className="qv-checklist-row"
                      onClick={() => setEditingChecklist({ id: fc.id })}
                      style={{ borderColor: isActive ? '#2563eb' : fc.all_complete ? '#86efac' : '#e5e7eb',
                        background: isActive ? '#eff6ff' : fc.all_complete ? '#f0fdf4' : '#fff' }}>
                      <div>
                        <strong>{fc.name || 'Field Checklist'}</strong>
                        <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 8 }}>
                          {fc.completed_by_name ? `by ${fc.completed_by_name}` : ''}{fc.created_at ? ` · ${new Date(fc.created_at).toLocaleDateString()}` : ''}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="qv-chip" style={{
                          background: isActive ? '#dbeafe' : fc.all_complete ? '#dcfce7' : '#fef3c7',
                          color: isActive ? '#1e40af' : fc.all_complete ? '#166534' : '#92400e',
                        }}>{isActive ? 'In Progress' : fc.all_complete ? 'Complete' : 'Incomplete'}</span>
                        {canManagePayroll && (
                          <button className="qv-del-btn" title="Delete"
                            onClick={(e) => { e.stopPropagation(); deleteChecklist(fc); }}><FiTrash2 size={13} /></button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <div className="dash-mini-empty">No field checklists yet for this job.</div>}
          </div>
        )}

        {activeTab === 'Parcels' && (
          <div className="dash-panel">
            <div className="qv-tab-headrow">
              <h3 className="qv-tabhead">Parcel Data</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={() => setParcelImportOpen(true)}>
                  <FiPlus /> Import from County Records
                </button>
                <label className="btn btn-secondary btn-sm" style={{ cursor: uploadingParcels ? 'wait' : 'pointer' }}>
                  <FiUpload /> {uploadingParcels ? 'Uploading…' : 'Upload JSON'}
                  <input type="file" accept=".json" onChange={handleParcelsUpload} disabled={uploadingParcels} style={{ display: 'none' }} />
                </label>
                {parcels && parcels.length > 0 && (
                  <button className="btn btn-secondary btn-sm" onClick={clearParcels}>Clear All</button>
                )}
              </div>
            </div>
            {parcels && parcels.length > 0 ? (
              <table className="dash-prof-table">
                <thead><tr><th>Parcel ID</th><th>Owner</th><th>Address</th><th style={{ textAlign: 'right' }}>Acres</th><th>LL / Dist</th><th style={{ width: 50 }}></th></tr></thead>
                <tbody>
                  {parcels.map(p => (
                    <tr key={p.id}>
                      <td>{p.parcel_url ? <a href={p.parcel_url} target="_blank" rel="noopener noreferrer">{p.parcel_id}</a> : p.parcel_id}</td>
                      <td>{p.owner}</td>
                      <td>{p.property_address}</td>
                      <td style={{ textAlign: 'right' }}>{p.acres}</td>
                      <td>{[p.land_lot, p.district].filter(Boolean).join(' / ')}</td>
                      <td className="qv-row-actions"><button className="qv-del-btn" title="Remove" onClick={() => removeParcel(p)}><FiTrash2 size={13} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="dash-mini-empty">No parcel data imported. Import from county records, or upload a JSON export.</div>}

            <h3 className="qv-tabhead" style={{ marginTop: '1.75rem' }}>Property Boundary</h3>
            <div className="qv-boundary-row">
              {job.boundary_file ? (
                <>
                  <span style={{ color: '#059669', fontWeight: 600 }}>✓ Boundary uploaded</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Link to="/map" className="btn btn-secondary btn-sm">View on Map</Link>
                    <button className="btn btn-secondary btn-sm" onClick={deleteBoundary}>Remove</button>
                  </div>
                </>
              ) : (
                <>
                  <span style={{ color: '#9ca3af' }}>No boundary file uploaded</span>
                  <label className="btn btn-secondary btn-sm" style={{ cursor: uploadingBoundary ? 'wait' : 'pointer' }}>
                    <FiUpload /> {uploadingBoundary ? 'Uploading…' : 'Upload KML/KMZ'}
                    <input type="file" accept=".kml,.kmz,.geojson" onChange={handleBoundaryUpload} disabled={uploadingBoundary} style={{ display: 'none' }} />
                  </label>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'Letters' && (
          <div className="dash-panel">
            <RelatedLetters relatedType="job" relatedId={job.id} />
          </div>
        )}

        {activeTab === 'Comments' && (
          <div className="dash-panel">
            <JobComments job={job} onJobUpdated={loadJob} />
          </div>
        )}

        {activeTab === 'Portal' && (
          <div className="dash-panel">
            {portal ? (
              <>
                <div className="qv-tab-headrow">
                  <h3 className="qv-tabhead">Client Portal</h3>
                  <button className={`btn btn-sm ${portal.is_active ? 'btn-secondary' : 'btn-primary'}`} onClick={togglePortal}>
                    {portal.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  Status: <strong style={{ color: portal.is_active ? '#059669' : '#9ca3af' }}>
                    {portal.is_active ? 'Active' : 'Inactive'}
                  </strong>
                </div>
                {portal.is_active && portal.token && (
                  <>
                    <div className="qv-field" style={{ maxWidth: 520 }}>
                      <label>Public Link</label>
                      <input type="text" readOnly value={`${window.location.origin}/portal/${portal.token}`}
                        onFocus={(e) => e.target.select()} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, margin: '0.75rem 0 1.25rem', flexWrap: 'wrap' }}>
                      {!job.has_phases && (
                        <button className="btn btn-sm" style={{ background: '#16a34a', color: '#fff' }} disabled={enRouteBusy} onClick={sendWholeJobEnRoute}>
                          🚗 {enRouteBusy ? 'Sending…' : 'En Route — Notify Client'}
                        </button>
                      )}
                      <a className="btn btn-secondary btn-sm" href={`/portal/${portal.token}`} target="_blank" rel="noopener noreferrer">Open Portal</a>
                      <button className="btn btn-secondary btn-sm" onClick={copyPortalLink}><FiLink /> Copy link</button>
                      <button className="btn btn-secondary btn-sm" onClick={textPortalLink}><FiMessageSquare /> Text link</button>
                      <button className="btn btn-primary btn-sm" onClick={openPortalMessage}><FiMail /> Message Client</button>
                      <button className="btn btn-secondary btn-sm" onClick={emailPortalLink}><FiMail /> Email link to client</button>
                      <button className="btn btn-secondary btn-sm" onClick={openNotify}><FiSend /> Notify contacts</button>
                      <button className="btn btn-secondary btn-sm" onClick={toggleDigest}>
                        Weekly digest: {portal.weekly_digest_enabled ? 'On' : 'Off'}
                      </button>
                    </div>
                  </>
                )}
                <h3 className="qv-tabhead">Recent Activity</h3>
                {portalNotifs && portalNotifs.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {portalNotifs.slice(0, 15).map(n => (
                      <div key={n.id} style={{ borderBottom: '1px solid var(--bg-tertiary)', paddingBottom: 8 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{n.title}</div>
                        {n.message && <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{n.message}</div>}
                        {n.created_at && <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{new Date(n.created_at + 'Z').toLocaleString()}</div>}
                      </div>
                    ))}
                  </div>
                ) : <div className="dash-mini-empty">No portal activity yet</div>}
              </>
            ) : <div className="dash-mini-empty">Loading portal…</div>}
          </div>
        )}

        {activeTab === 'Profitability' && (
          <div className="dash-panel">
            {!canManagePayroll ? (
              <div className="dash-mini-empty">You don't have access to financial data.</div>
            ) : profit ? (
              <>
                <div className="jo-profit" style={{ marginTop: 0, borderTop: 'none', paddingTop: 0 }}>
                  <div className="jo-profit-card"><div className="jo-profit-val">{fmtMoney(profit.revenue)}</div><div className="jo-profit-lbl">Revenue</div></div>
                  <div className="jo-profit-card"><div className="jo-profit-val">{fmtMoney(profit.total_costs)}</div><div className="jo-profit-lbl">Total Costs</div></div>
                  <div className="jo-profit-card"><div className="jo-profit-val" style={{ color: profit.profit < 0 ? '#dc2626' : '#059669' }}>{fmtMoney(profit.profit)}</div><div className="jo-profit-lbl">Profit</div></div>
                  <div className="jo-profit-card"><div className="jo-profit-val" style={{ color: profit.margin_percent < 0 ? '#dc2626' : '#1f2937' }}>{profit.margin_percent}%</div><div className="jo-profit-lbl">Margin</div></div>
                </div>
                {profit.costs && (
                  <table className="dash-prof-table" style={{ marginTop: '1.25rem' }}>
                    <thead><tr><th>Cost</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
                    <tbody>
                      <tr><td>Labor ({profit.costs.labor_hours}h)</td><td style={{ textAlign: 'right' }}>{fmtMoney(profit.costs.labor)}</td></tr>
                      <tr><td>Mileage ({(profit.costs.distance_miles / 2).toFixed(1)} mi each way, {profit.costs.distance_miles.toFixed(1)} round trip{profit.costs.distance_source === 'straight_line' ? ' · straight-line estimate' : ''})</td><td style={{ textAlign: 'right' }}>{fmtMoney(profit.costs.mileage)}</td></tr>
                      <tr><td>Materials</td><td style={{ textAlign: 'right' }}>{fmtMoney(profit.costs.materials)}</td></tr>
                      <tr><td>Overhead</td><td style={{ textAlign: 'right' }}>{fmtMoney(profit.costs.overhead)}</td></tr>
                      <tr><td>Other</td><td style={{ textAlign: 'right' }}>{fmtMoney(profit.costs.other)}</td></tr>
                    </tbody>
                  </table>
                )}
                {estimate && (estimate.drive || estimate.field_hours) && (
                  <div style={{ marginTop: '1.25rem', padding: '12px 14px', background: 'var(--bg-secondary, #f8fafc)', borderRadius: 8, fontSize: '0.88rem' }}>
                    <h3 className="qv-tabhead" style={{ margin: '0 0 6px' }}>Estimate vs. Logged</h3>
                    {estimate.drive && (
                      <div>
                        <strong>Drive:</strong> {estimate.drive.one_way_minutes} min each way from the office ({estimate.drive.one_way_miles} mi)
                        {' '}<span style={{ color: '#64748b' }}>· Travel logged: {estimate.logged.travel_hours}h</span>
                      </div>
                    )}
                    <div style={{ marginTop: 4 }}>
                      <strong>Field time:</strong>{' '}
                      {estimate.field_hours?.median_hours != null ? (
                        <>typical {estimate.field_hours.scope}{estimate.field_hours.acreage_bucket ? ` (${estimate.field_hours.acreage_bucket})` : ''} job is {estimate.field_hours.median_hours} crew-hours
                          <span style={{ color: '#64748b' }}> (median of {estimate.field_hours.sample_size})</span></>
                      ) : estimate.field_hours ? (
                        <span style={{ color: '#64748b' }}>not enough finished {estimate.field_hours.scope} jobs to estimate yet</span>
                      ) : (
                        <span style={{ color: '#64748b' }}>set a service type to compare against similar jobs</span>
                      )}
                      {' '}<span style={{ color: '#64748b' }}>· Field logged: {estimate.logged.field_hours}h</span>
                    </div>
                  </div>
                )}
                {expenses && (
                  <div style={{ marginTop: '1.5rem', borderTop: '1px solid var(--bg-tertiary)', paddingTop: '1rem' }}>
                    <h3 className="qv-tabhead" style={{ marginBottom: 10 }}>Job Expenses <span style={{ fontWeight: 400, fontSize: 12, color: '#94a3b8' }}>(feeds the numbers above)</span></h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, maxWidth: 640 }}>
                      {[
                        ['rebar_count', 'Rebar (count)'],
                        ['pipe_count', 'Pipe/caps (count)'],
                        ['num_trips', 'Trips'],
                        ['crew_size', 'Crew vehicles'],
                        ['other_expenses', 'Other $'],
                      ].map(([key, label]) => (
                        <div key={key} className="qv-field">
                          <label>{label}</label>
                          <input type="number" step="any" value={expenses[key] ?? ''}
                            onChange={(e) => setExpenses(prev => ({ ...prev, [key]: e.target.value }))} />
                        </div>
                      ))}
                    </div>
                    <div className="qv-field" style={{ maxWidth: 640, marginTop: 10 }}>
                      <label>Other expenses — note</label>
                      <input type="text" value={expenses.other_expenses_note || ''}
                        onChange={(e) => setExpenses(prev => ({ ...prev, other_expenses_note: e.target.value }))} />
                    </div>
                    <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} disabled={savingExpenses} onClick={saveExpenses}>
                      {savingExpenses ? 'Saving…' : 'Update Expenses & Recalculate'}
                    </button>
                  </div>
                )}
              </>
            ) : <div className="dash-mini-empty">Loading profitability…</div>}
          </div>
        )}

        {activeTab === 'Documents' && (
          <div className="dash-panel"><JobAttachments jobId={job.id} phases={job.has_phases ? (job.phases || []) : []} /></div>
        )}

        {activeTab === 'Pictures' && (
          <div className="dash-panel">
            <div className="qv-tab-headrow">
              <h3 className="qv-tabhead">Pictures</h3>
              <div className="qv-tab-actions qv-pics-actions">
                <label className="btn btn-primary btn-sm" style={{ cursor: uploadingPhoto ? 'wait' : 'pointer' }}>
                  <FiCamera /> {uploadingPhoto ? 'Uploading…' : 'Take Photo'}
                  <input type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} disabled={uploadingPhoto} style={{ display: 'none' }} />
                </label>
                <label className="btn btn-sm" style={{ cursor: uploadingPhoto ? 'wait' : 'pointer' }}>
                  <FiUpload /> {uploadingPhoto ? 'Uploading…' : 'Upload'}
                  <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} disabled={uploadingPhoto} style={{ display: 'none' }} />
                </label>
                {photos && photos.length > 0 && (
                  <button className="btn btn-sm" style={{ background: '#7c3aed', color: '#fff' }} onClick={() => setShowPlacePhotos(true)} title="Place photos at exact boundary corners on the map">
                    <FiMapPin /> Place on Map
                  </button>
                )}
                {photos && photos.length > 0 && (
                  <button className="btn btn-sm" style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac' }} onClick={copyShareLink} title="Share the client portal link (photos marked 'Show on portal')">
                    <FiShare2 /> Share
                  </button>
                )}
                {viewablePhotos.length > 0 && (
                  <button
                    className="btn btn-sm"
                    onClick={downloadAllPhotos}
                    disabled={!!bulkDl}
                    title="Save every photo on this job to your computer"
                    style={{ cursor: bulkDl ? 'wait' : 'pointer' }}
                  >
                    <FiDownload /> {bulkDl ? `Downloading ${bulkDl.done}/${bulkDl.total}...` : 'Download All'}
                  </button>
                )}
                {photos && photos.length > 0 && (
                  <button className="btn btn-sm qv-pics-email" style={{ background: '#16a34a', color: '#fff' }} onClick={() => setShowPhotoEmail(true)}>
                    <FiMail /> Email to Client
                  </button>
                )}
              </div>
            </div>
            {allPhotos.length > 0 ? (
              <div className="qv-gallery">
                {allPhotos.map(p => (
                  <div key={p.id} className="qv-photo">
                    <button
                      type="button"
                      onClick={() => !p.__queued && setLightboxPhoto(p)}
                      style={{ padding: 0, border: 'none', background: 'none', cursor: p.__queued ? 'default' : 'pointer', width: '100%', display: 'block' }}
                      aria-label="View photo"
                    >
                      {/* Queued photos carry a blob: object URL, not a server path. */}
                      <img
                        src={p.__queued ? p.url : `${API_BASE_URL || ''}${p.url}`}
                        alt={p.caption || p.original_filename || 'Job photo'}
                        style={p.__queued ? { opacity: 0.65 } : undefined}
                      />
                    </button>
                    {p.__queued ? (
                      <span
                        title="Saved on this device — uploads automatically when you have signal"
                        style={{
                          position: 'absolute', left: 6, top: 6,
                          fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                          background: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b',
                        }}
                      >
                        Waiting to upload
                      </span>
                    ) : (
                      <>
                        <button className="qv-photo-dl" title="Download" aria-label="Download photo" onClick={() => downloadPhoto(p)}><FiDownload size={13} /></button>
                        <button className="qv-photo-del" title="Delete" onClick={() => deletePhoto(p)}><FiTrash2 size={13} /></button>
                        <button
                          onClick={() => openPhotoMarkup(p)}
                          title={p.has_markup ? 'Edit markup' : 'Draw, add text and place BMP blocks'}
                          style={{
                            position: 'absolute', right: 6, bottom: 6,
                            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                            cursor: 'pointer', border: '1px solid',
                            borderColor: p.has_markup ? '#93c5fd' : 'rgba(255,255,255,0.6)',
                            background: p.has_markup ? '#dbeafe' : 'rgba(0,0,0,0.55)',
                            color: p.has_markup ? '#1e40af' : '#fff',
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                          }}
                        >
                          <FiEdit3 size={11} /> {p.has_markup ? 'Marked up' : 'Mark up'}
                        </button>
                      </>
                    )}
                    {!p.__queued && p.photo_lat != null && (
                      <button
                        onClick={() => togglePortalPhoto(p)}
                        title={p.show_on_portal ? 'Showing on client portal map — click to hide' : 'Not shared — click to show on client portal map'}
                        style={{
                          position: 'absolute', left: 6, bottom: 6,
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                          cursor: 'pointer', border: '1px solid',
                          borderColor: p.show_on_portal ? '#86efac' : 'rgba(255,255,255,0.6)',
                          background: p.show_on_portal ? '#dcfce7' : 'rgba(0,0,0,0.55)',
                          color: p.show_on_portal ? '#166534' : '#fff',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <FiMapPin size={11} /> {p.show_on_portal ? 'On portal' : 'Show on portal'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : <div className="dash-mini-empty">No pictures uploaded</div>}
          </div>
        )}

        {activeTab === 'Finances' && canManagePayroll && (
          <div className="dash-panel">
            <div className="dash-fin-grid">
              <div className="dash-fin-card"><div className="dash-fin-value">{fmtMoney(job.contract_amount)}</div><div className="dash-fin-label">Contract Amount</div></div>
              <div className="dash-fin-card"><div className="dash-fin-value">{fmtMoney(job.change_orders_total)}</div><div className="dash-fin-label">Change Orders</div></div>
              <div className="dash-fin-card"><div className="dash-fin-value">{fmtMoney(job.total_contract_value)}</div><div className="dash-fin-label">Total Contract Value</div></div>
              {canManagePayroll && profit && (
                <div className="dash-fin-card"><div className="dash-fin-value">{fmtMoney(profit.revenue)}</div><div className="dash-fin-label">Invoiced Revenue</div></div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              <button
                className="btn btn-secondary btn-sm"
                disabled={billingBusy === 'quote'}
                onClick={() => createFromJob('quote')}
                title="Start a draft quote pre-filled from this job"
              >
                <FiFileText /> {billingBusy === 'quote' ? 'Creating…' : 'Create Quote'}
              </button>
              <button
                className="btn btn-secondary btn-sm"
                disabled={billingBusy === 'invoice'}
                onClick={() => createFromJob('invoice')}
                title="Start a draft invoice pre-filled from this job, including any change orders"
              >
                <FiFileText /> {billingBusy === 'invoice' ? 'Creating…' : 'Create Invoice'}
              </button>
            </div>
            <h4 style={{ margin: '20px 0 8px', fontSize: 14, color: '#374151' }}>
              Invoices{jobInvoices && jobInvoices.length > 0 ? ` (${jobInvoices.length})` : ''}
            </h4>
            {jobInvoices === null ? (
              <div className="dash-mini-empty">Loading invoices…</div>
            ) : jobInvoices.length === 0 ? (
              <div className="dash-mini-empty">No invoices on this job yet</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="dash-prof-table">
                  <thead>
                    <tr>
                      <th>Invoice #</th><th>Date</th><th>Bill To</th>
                      {job.has_phases && <th>Phase</th>}
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th style={{ textAlign: 'right' }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobInvoices.map(inv => {
                      const phase = (job.phases || []).find(p => p.id === inv.phase_id);
                      const statusColor = {
                        Paid: '#16a34a', Sent: '#2563eb', Overdue: '#dc2626', Draft: '#6b7280',
                        'Partially Paid': '#d97706', Imported: '#6b7280', Void: '#9ca3af', Cancelled: '#9ca3af',
                      }[inv.status] || '#6b7280';
                      return (
                        <tr key={inv.id} onClick={() => navigate(`/billing/invoices/${inv.id}`)} style={{ cursor: 'pointer' }}>
                          <td><Link to={`/billing/invoices/${inv.id}`} onClick={e => e.stopPropagation()}>{inv.invoice_number}</Link></td>
                          {/* invoice_date is date-only: parse at local noon, never append 'Z' */}
                          <td>{inv.invoice_date ? new Date(inv.invoice_date + 'T12:00:00').toLocaleDateString() : '—'}</td>
                          <td>{inv.bill_to_company || inv.bill_to_name || inv.client_name || '—'}</td>
                          {job.has_phases && <td>{phase ? phase.name : '—'}</td>}
                          <td><span style={{ color: statusColor, fontWeight: 600, textDecoration: (inv.status === 'Void' || inv.status === 'Cancelled') ? 'line-through' : 'none' }}>{inv.status}</span></td>
                          <td style={{ textAlign: 'right' }}>{fmtCents(inv.total)}</td>
                          <td style={{ textAlign: 'right' }}>{fmtCents(inv.balance_due)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div className="qv-tabfoot"><Link to="/billing">Open billing & invoices →</Link></div>
          </div>
        )}
      </div>

      {/* Photo lightbox - full-screen viewer: X close, prev/next, Download, Delete */}
      {markupPhoto && markupUrl && (
        <PhotoAnnotator
          imageUrl={markupUrl}
          annotationJson={markupPhoto.annotation_json}
          bmpSymbols={bmpSymbols}
          title={`Mark Up — ${markupPhoto.original_filename || 'Job Photo'}`}
          onSave={savePhotoMarkup}
          onClose={closePhotoMarkup}
        />
      )}

      {lightboxPhoto && (() => {
        const isPhone = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || window.innerWidth < 768;
        const list = viewablePhotos;
        const idx = list.findIndex(p => p.id === lightboxPhoto.id);
        const hasNav = list.length > 1 && idx !== -1;
        const go = (delta) => {
          if (!hasNav) return;
          setLightboxPhoto(list[(idx + delta + list.length) % list.length]);
        };
        const handleDelete = async () => {
          if (!window.confirm('Delete this photo?')) return;
          try {
            await axios.delete(`${API_URL}/jobs/${id}/photos/${lightboxPhoto.id}`, await getAuthConfig());
            // Stay in the viewer and move to the next shot rather than dumping the
            // user back to the grid after every delete.
            setLightboxPhoto(hasNav ? list[(idx + 1) % list.length] : null);
            await loadPhotos();
          } catch (e) { alert('Could not delete'); }
        };
        const navBtn = (side) => ({
          position: 'absolute',
          [side]: isPhone ? '6px' : '24px',
          top: '50%', transform: 'translateY(-50%)',
          width: isPhone ? '44px' : '52px', height: isPhone ? '44px' : '52px',
          borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.15)', color: 'white',
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2,
        });
        return (
          <div
            onClick={() => setLightboxPhoto(null)}
            onTouchStart={(e) => { swipeRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
            onTouchEnd={(e) => {
              const start = swipeRef.current;
              swipeRef.current = null;
              if (!start || !e.changedTouches?.length) return;
              const dx = e.changedTouches[0].clientX - start.x;
              const dy = e.changedTouches[0].clientY - start.y;
              // Horizontal swipe only - vertical drags are scroll/dismiss gestures.
              if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1);
            }}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.95)',
              display: 'flex', flexDirection: 'column',
              justifyContent: 'center', alignItems: 'center',
              // Above the mobile bottom nav (z 99980) so the photo actually fills the screen
              zIndex: 999999,
              padding: isPhone ? '12px' : '20px',
              paddingTop: isPhone ? 'max(12px, env(safe-area-inset-top))' : '20px',
              paddingBottom: isPhone ? 'max(12px, env(safe-area-inset-bottom))' : '20px',
            }}
          >
            {/* Big X close - top-left, thumb-friendly */}
            <button
              onClick={() => setLightboxPhoto(null)}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: isPhone ? 'max(12px, env(safe-area-inset-top))' : '20px',
                left: isPhone ? '12px' : '20px',
                width: '44px', height: '44px', borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.15)', color: 'white',
                border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 2,
              }}
            >
              <FiX size={24} />
            </button>

            {/* Action buttons - icon-only on phone. Download is open to everyone;
                deleting a job photo stays an office action. */}
            <div style={{
              position: 'absolute',
              top: isPhone ? 'max(12px, env(safe-area-inset-top))' : '20px',
              right: isPhone ? '12px' : '20px',
              display: 'flex', gap: '8px', zIndex: 2,
            }}>
              <button
                onClick={(e) => { e.stopPropagation(); downloadPhoto(lightboxPhoto); }}
                aria-label="Download"
                title="Download this photo"
                style={{
                  padding: isPhone ? '0' : '10px 20px',
                  width: isPhone ? '44px' : 'auto',
                  height: isPhone ? '44px' : 'auto',
                  borderRadius: isPhone ? '50%' : '6px',
                  backgroundColor: '#28a745', color: 'white', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}
              >
                <FiDownload size={isPhone ? 20 : 16} /> {!isPhone && 'Download'}
              </button>
              {canManagePayroll && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                  aria-label="Delete"
                  style={{
                    padding: isPhone ? '0' : '10px 20px',
                    width: isPhone ? '44px' : 'auto',
                    height: isPhone ? '44px' : 'auto',
                    borderRadius: isPhone ? '50%' : '6px',
                    backgroundColor: '#dc3545', color: 'white', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  }}
                >
                  <FiTrash2 size={isPhone ? 20 : 16} /> {!isPhone && 'Delete'}
                </button>
              )}
            </div>

            {hasNav && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); go(-1); }}
                  aria-label="Previous photo"
                  title="Previous (left arrow)"
                  style={navBtn('left')}
                >
                  <FiChevronLeft size={isPhone ? 26 : 32} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); go(1); }}
                  aria-label="Next photo"
                  title="Next (right arrow)"
                  style={navBtn('right')}
                >
                  <FiChevronRight size={isPhone ? 26 : 32} />
                </button>
              </>
            )}

            <img
              key={lightboxPhoto.id}
              src={`${API_BASE_URL || ''}${lightboxPhoto.url}`}
              alt={lightboxPhoto.original_filename || 'Job photo'}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: isPhone ? '100%' : '90%',
                maxHeight: isPhone ? 'calc(100dvh - 160px)' : '80vh',
                objectFit: 'contain', borderRadius: '8px',
              }}
            />
            {lightboxPhoto.caption && (
              <div style={{ color: 'white', marginTop: '12px', textAlign: 'center', fontSize: '13px', opacity: 0.85, maxWidth: '90%' }}>
                {lightboxPhoto.caption}
              </div>
            )}
            {idx !== -1 && list.length > 1 && (
              <div style={{ color: 'white', marginTop: '10px', fontSize: '12px', opacity: 0.6, letterSpacing: '0.03em' }}>
                {idx + 1} of {list.length}
              </div>
            )}
          </div>
        );
      })()}

      {showPhotoEmail && (
        <EmailToClientModal
          jobId={job.id}
          items={(photos || []).map(p => ({ id: p.id, kind: 'photo', name: p.original_filename || `photo-${p.id}`, mime: 'image/jpeg', thumbUrl: `${API_BASE_URL || ''}${p.url}`, size: p.file_size, caption: p.caption }))}
          onClose={() => setShowPhotoEmail(false)}
        />
      )}

      {showLocationPicker && (
        <JobLocationPickerModal
          jobId={id}
          job={job}
          onClose={() => setShowLocationPicker(false)}
          onSaved={loadJob}
        />
      )}

      {/* Printable work order (job header, billing/location, time table, notes, signature) */}
      {showWorkOrder && (
        <WorkOrderPrint
          job={job}
          timeEntries={Array.isArray(timeEntries) ? timeEntries : []}
          onClose={() => setShowWorkOrder(false)}
        />
      )}

      {/* Cancel job */}
      {showCancel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => !cancelling && setShowCancel(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 14, padding: 22, width: '100%', maxWidth: 460 }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 17 }}>Cancel Job {job.job_number}</h3>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748b' }}>This pulls the job off the schedule, cancels linked quotes, and turns off the client portal. Your team is notified; the client is not.</p>
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Reason *</label>
            <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. Client went with another firm"
              style={{ width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', marginBottom: 10, fontSize: 14, boxSizing: 'border-box' }} />
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Notes (optional)</label>
            <textarea value={cancelNotes} onChange={(e) => setCancelNotes(e.target.value)} rows={3}
              style={{ width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', marginBottom: 16, fontSize: 14, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-sm" onClick={() => setShowCancel(false)} disabled={cancelling}>Keep Job</button>
              <button className="btn btn-primary btn-sm" style={{ background: '#dc2626' }} onClick={handleCancelJob} disabled={cancelling}>{cancelling ? 'Cancelling…' : 'Cancel Job'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Notify selected contacts */}
      {showNotify && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => !notifyBusy && setShowNotify(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 14, padding: 22, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 17 }}>Notify Contacts</h3>
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Who</label>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 8, marginBottom: 12, maxHeight: 180, overflowY: 'auto' }}>
              {(contacts || []).length === 0 ? <div style={{ fontSize: 13, color: '#94a3b8' }}>No contacts on file.</div> :
                (contacts || []).map((c, i) => (
                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', fontSize: 13.5 }}>
                    <input type="checkbox" checked={notifySel.includes(i)} onChange={() => toggleNotifySel(i)} style={{ width: 'auto' }} />
                    <span>{c.name}{c.role ? ` · ${c.role}` : ''}{(!c.phone && !c.email) ? ' (no phone/email)' : ''}</span>
                  </label>
                ))}
            </div>
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Title</label>
            <input value={notifyTitle} onChange={(e) => setNotifyTitle(e.target.value)} placeholder="e.g. Schedule update"
              style={{ width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', marginBottom: 10, fontSize: 14, boxSizing: 'border-box' }} />
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Message</label>
            <textarea value={notifyMsg} onChange={(e) => setNotifyMsg(e.target.value)} rows={3}
              style={{ width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', marginBottom: 12, fontSize: 14, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5 }}><input type="checkbox" checked={notifySms} onChange={(e) => setNotifySms(e.target.checked)} style={{ width: 'auto' }} /> Text</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5 }}><input type="checkbox" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} style={{ width: 'auto' }} /> Email</label>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-sm" onClick={() => setShowNotify(false)} disabled={notifyBusy}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={sendNotify} disabled={notifyBusy}>{notifyBusy ? 'Sending…' : `Send to ${notifySel.length}`}</button>
            </div>
          </div>
        </div>
      )}

      {/* Per-section edit modal (Details tab) */}
      {editSection && (
        <JobFieldEditModal
          jobId={id}
          title={editSection.title}
          fields={editSection.fields}
          job={job}
          onClose={() => setEditSection(null)}
          onSaved={loadJob}
        />
      )}

      {/* Parent-job link modal (Related Jobs card) */}
      {editParent && (
        <ParentJobModal
          jobId={id}
          parentId={job.parent_job_id}
          parentNumber={job.parent_job_number}
          onClose={() => setEditParent(false)}
          onSaved={loadJob}
        />
      )}

      {showPlacePhotos && (
        <PlacePhotosModal
          jobId={id}
          onClose={() => setShowPlacePhotos(false)}
          onSaved={loadPhotos}
        />
      )}

      {woAttachments && (
        <WorkOrderAttachmentsModal
          jobId={id}
          workOrder={woAttachments}
          onClose={() => setWoAttachments(null)}
        />
      )}

      {/* Generic create/edit modal (work orders, change orders, contacts, time entries) */}
      {formModal && (
        <FormModal
          title={formModal.title}
          fields={formModal.fields}
          initial={formModal.initial}
          submitLabel={formModal.submitLabel}
          onSubmit={formModal.onSubmit}
          onClose={() => setFormModal(null)}
        />
      )}

      {/* Field checklist editor */}
      {editingChecklist && (
        <FieldChecklistModal
          jobId={job.id}
          jobNumber={job.job_number}
          checklistId={editingChecklist.id}
          mode="edit"
          onComplete={() => { setEditingChecklist(null); loadChecklists(); }}
          onClose={() => { setEditingChecklist(null); loadChecklists(); }}
        />
      )}

      {/* Parcel import browser */}
      {parcelImportOpen && (
        <ParcelImportModal
          jobId={id}
          onClose={() => setParcelImportOpen(false)}
          onImported={(count) => { setParcelImportOpen(false); loadParcels(); alert(`Imported ${count} parcel(s).`); }}
        />
      )}
    </div>
  );
}
